import React, { useState, useEffect, useRef, useMemo } from 'react';
import { collection, addDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate, useLocation } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { useTheme } from '../context/ThemeContext';
import TemplatesPickerModal from '../components/TemplatesPickerModal';
import LineItemEditorModal from '../components/LineItemEditorModal';
import ProductPickerModal from '../components/ProductPickerModal';
import EstimatorMenu from '../components/EstimatorMenu';
import {
  getPaymentStatus, isSettled, PAYMENT_STATUS, paymentMirrorFields,
  syncInvoicePaymentMirror, mirrorSyncFailedMessage,
} from '../utils/paymentStatus';
import { generateDocNumber as reserveDocNumber } from '../utils/docNumber';
import {
  lineTotal, lineCost, computeDocTotals, round2, buildCostLookup, resolveUnitCost,
} from '../utils/lineMath';
import {
  isOnPlate, setLineOnPlate, editLineField, setLineBasePrice, reconcileEditedLine, ON_PLATE_SURCHARGE,
} from '../utils/onPlate';
import { roundLineItems } from '../utils/packageRounding';
import {
  readEstimates, applyEstimatorImport, estimatorImportKey,
  estimatorSpecsMirror, pendingModify, removeEstimate, PENDING_ADD,
} from '../utils/estimates';
import { EstimateModifyControl, EstimatesList } from '../components/EstimatesPanel';
import { getAllPhoneNumbers } from '../utils/customerPhone';
import { reserveCustomerNumber } from '../utils/customerNumber';
import { BOARD_STATUS_OPTIONS } from '../utils/boardColumns';
import CreditHoldWarning from '../components/CreditHoldWarning';

const CATEGORIES = [
  'Chain Link',
  'Galv Fittings',
  'Black Fittings',
  'Galv Pipe',
  'Black Pipe',
  'Residential Swing Gates Galv',
  'Commercial Swing Gates Galv',
  'Residential Gates Black',
  'Commercial Gates Black',
  'Wood',
  'Wood Hardware',
  'Split Rail',
  'Tools/Misc',
  'PVC',
  'Cantilever Gate',
  'Aluminum Slide Gate Hardware',
  'Aluminum',
];

// Draft persistence for round-trips through the Estimator (which would otherwise
// unmount this component and lose state). Single consolidated JSON per docType.
const draftKey = (docType) => `docDraft_new_${docType}`;
const saveDraft = (docType, data) => {
  try { sessionStorage.setItem(draftKey(docType), JSON.stringify(data)); } catch {}
};
const loadDraft = (docType) => {
  try {
    const raw = sessionStorage.getItem(draftKey(docType));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
const clearDraft = (docType) => {
  try { sessionStorage.removeItem(draftKey(docType)); } catch {}
};

// Inventory alert status for a product being added to a document. Returns null
// when no alert is warranted (untracked product, or sufficient stock).
const getStockStatus = (product, requestedQty = 1) => {
  if (!product || !product.trackInventory) return null;
  const stock = parseFloat(product.stockQty) || 0;
  const reorder = parseFloat(product.reorderPoint) || 0;
  if (stock <= 0) return { level: 'out', message: 'Out of Stock', stockQty: stock, requestedQty };
  if (stock < requestedQty) return { level: 'insufficient', message: 'Not Enough Stock', stockQty: stock, requestedQty };
  if (stock <= reorder) return { level: 'low', message: 'Low Stock', stockQty: stock, requestedQty };
  return null;
};

function NewDocument({ user, docType = 'quote' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const userName = user?.email?.split('@')[0]?.toUpperCase();
  const { products, customers, addCustomerToCache } = useData();
  const { theme, themeName } = useTheme();
  const isDark = themeName === 'dark';

  const C_TEXT = theme.textPrimary;
  const C_TEXT_2 = theme.textSecondary;
  const C_TEXT_3 = theme.textTertiary;
  const C_BORDER = theme.borderSubtle;
  const C_BG_CARD = theme.bgCard;
  const C_BG_CARD_SOLID = theme.bgCardSolid;
  const C_BG_NESTED = theme.bgNested;
  const C_CYAN = theme.cyan;
  const C_CYAN_BRIGHT = theme.cyanBright;
  const C_VIOLET = theme.violet;
  const C_VIOLET_BRIGHT = theme.violetBright;
  const C_GREEN = theme.green;
  const C_GREEN_BRIGHT = theme.greenBright;
  const C_ORANGE = theme.orange;
  const C_ORANGE_BRIGHT = theme.orangeBright;
  const C_RED = theme.red;
  const C_RED_BRIGHT = theme.redBright;
  const GRADIENT = `linear-gradient(135deg, ${C_CYAN} 0%, ${C_VIOLET} 100%)`;
  const LEVEL_COLORS = {
    A: { bg: `${C_CYAN}26`, border: `${C_CYAN}80`, text: C_CYAN_BRIGHT },
    B: { bg: `${C_GREEN}26`, border: `${C_GREEN}80`, text: C_GREEN_BRIGHT },
    C: { bg: `${C_ORANGE}26`, border: `${C_ORANGE}80`, text: C_ORANGE_BRIGHT },
  };
  const neutralBg = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)';
  const neutralHover = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)';

  const cardStyle = {
    background: C_BG_CARD,
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: `1px solid ${C_BORDER}`,
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
  };
  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    background: theme.inputBg,
    border: `1px solid ${C_BORDER}`,
    borderRadius: 10,
    color: C_TEXT,
    fontSize: 14,
    outline: 'none',
    transition: 'border-color 0.3s, box-shadow 0.3s, background 0.3s',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };
  const labelStyle = {
    color: C_TEXT_2,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 6,
    display: 'block',
  };
  const tableHeaderCell = {
    padding: '12px 14px',
    textAlign: 'left',
    color: C_CYAN_BRIGHT,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
    borderBottom: `1px solid ${C_CYAN}33`,
  };
  const gradientButton = {
    background: GRADIENT, border: 'none', color: '#fff',
    padding: '12px 24px', borderRadius: 12,
    fontSize: 14, fontWeight: 700, cursor: 'pointer',
    letterSpacing: 0.5, boxShadow: `0 4px 20px ${C_CYAN}4D`,
    transition: 'transform 0.3s, box-shadow 0.3s',
  };
  const cyanGlassButton = {
    background: `${C_CYAN}1A`, border: `1px solid ${C_CYAN}4D`,
    color: C_CYAN_BRIGHT, padding: '10px 16px', borderRadius: 10,
    fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s',
  };
  const violetGlassButton = {
    background: `${C_VIOLET}1A`, border: `1px solid ${C_VIOLET}4D`,
    color: C_VIOLET_BRIGHT, padding: '10px 16px', borderRadius: 10,
    fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s',
  };
  const greenGlassButton = {
    background: `${C_GREEN}1A`, border: `1px solid ${C_GREEN}4D`,
    color: C_GREEN_BRIGHT, padding: '10px 16px', borderRadius: 10,
    fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s',
  };
  const orangeGlassButton = {
    background: `${C_ORANGE}1A`, border: `1px solid ${C_ORANGE}4D`,
    color: C_ORANGE_BRIGHT, padding: '10px 16px', borderRadius: 10,
    fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s',
  };
  const redGlassButton = {
    background: `${C_RED}1A`, border: `1px solid ${C_RED}4D`,
    color: C_RED_BRIGHT, padding: '8px 14px', borderRadius: 10,
    fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s',
  };
  const darkButton = {
    background: neutralBg, border: `1px solid ${C_BORDER}`,
    color: C_TEXT, padding: '10px 16px', borderRadius: 10,
    fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s',
  };
  const focusInputCyan = (e) => {
    e.target.style.borderColor = `${C_CYAN}80`;
    e.target.style.boxShadow = `0 0 0 3px ${C_CYAN}1A`;
  };
  const blurInputDefault = (e) => {
    e.target.style.borderColor = C_BORDER;
    e.target.style.boxShadow = 'none';
  };
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  // Per-document tax-exempt toggle. Defaults to the customer's flag on customer
  // change (see the inherit effect below); a manual toggle overrides it for THIS
  // document only and never touches the customer record.
  const [docTaxExempt, setDocTaxExempt] = useState(false);
  const prevCustIdRef = useRef(undefined);
  const canInheritTaxExemptRef = useRef(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '', phone: '', email: '', address: '', notes: '', taxExempt: false,
  });
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [possibleDuplicates, setPossibleDuplicates] = useState([]);
  const [pendingCustomerData, setPendingCustomerData] = useState(null);
  const [priceLevel, setPriceLevel] = useState('A');
  const [lineItems, setLineItems] = useState([]);
  // Every estimate on this document: [{ id, kind, specs, label }]. Lines carry
  // the estimateId of the estimate they came from. See utils/estimates.js.
  const [estimates, setEstimates] = useState([]);
  // Fingerprints of estimator returns already applied to this document's draft,
  // so revisiting the same return cannot import it twice.
  const appliedImportKeysRef = useRef([]);
  const [productSearch, setProductSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState('');
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [isDelivery, setIsDelivery] = useState(false);
  const [deliveryStreet, setDeliveryStreet] = useState('');
  const [deliveryCity, setDeliveryCity] = useState('');
  const [deliveryState, setDeliveryState] = useState('');
  const [deliveryZip, setDeliveryZip] = useState('');
  const [notes, setNotes] = useState('');
  const [docNumber, setDocNumber] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savingAndConverting, setSavingAndConverting] = useState(false);
  const [showNonStockForm, setShowNonStockForm] = useState(false);
  const [nonStockForm, setNonStockForm] = useState({ description: '', qty: 1, unit: 'pc', unitPrice: '', cost: '' });
  const [showTemplatesPicker, setShowTemplatesPicker] = useState(false);
  const [showOfficeUse, setShowOfficeUse] = useState(false);
  // Product costs, indexed by id AND by SKU. The two maps this replaced were
  // built here; they now come from buildCostLookup in utils/lineMath.js so the
  // line item editor indexes them identically. The SKU index is what recovers a
  // cost when a destructive re-import has orphaned a line's productId.
  // Memoized for the same reason as before: it is read once per line item.
  const costLookup = useMemo(() => buildCostLookup(products), [products]);

  // Payment state (invoice only)
  const [payments, setPayments] = useState([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [currentPayment, setCurrentPayment] = useState({
    amount: '', cashReceived: '', method: '', checkNumber: '',
    date: new Date().toISOString().split('T')[0], notes: '',
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [boardStatus, setBoardStatus] = useState('Ordered');
  const [editingItem, setEditingItem] = useState(null);
  const [showItemEditor, setShowItemEditor] = useState(false);
  // Manual line-item reordering: single-click selects a row (tracked by stable id),
  // ▲▼ buttons swap it with its neighbor in the array (array order = save/PDF order).
  const [selectedLineId, setSelectedLineId] = useState(null);
  const [showProductPicker, setShowProductPicker] = useState(false);
  // Inventory alerts when adding low/out-of-stock products
  const [stockAlertProduct, setStockAlertProduct] = useState(null);
  const [stockAlertStatus, setStockAlertStatus] = useState(null);
  const [batchStockAlerts, setBatchStockAlerts] = useState([]);
  const [pendingBatchProducts, setPendingBatchProducts] = useState(null);

  const mountedRef = useRef(false);
  const initializedRef = useRef(false);

  const TAX_RATE = 0.06;
  const today = new Date().toLocaleDateString('en-US');

  // ── Initialization + estimator-return handling ──
  // Consolidated into a single guarded effect so there is exactly one place that
  // initializes lineItems. (Splitting draft-restore and estimator-import across
  // two effects let StrictMode's double-invoke re-run the draft-restore and
  // clobber the imported items while the import's guard blocked re-importing.)
  // Re-prices the calculated estimator items against the loaded products list at
  // the document's level — the estimator computes prices at level A only, and
  // products may not be ready when it navigates back, so copying the incoming
  // prices verbatim would show wrong (level-A) prices. Waits for products first.
  useEffect(() => {
    if (initializedRef.current) return;

    const fromEstimator =
      !location.state?.keepCurrentItems &&
      location.state?.lineItems &&
      location.state.lineItems.length > 0;

    // Estimator items must be priced against the loaded products list.
    if (fromEstimator && (!products || products.length === 0)) return;

    initializedRef.current = true;

    // Restore any draft saved before a round-trip through the estimator.
    const draft = loadDraft(docType);
    let level = 'A';
    // Lines and estimates as restored from the draft, in the new shape. A draft
    // saved before this change holds estimatorSpecs, and is converted here.
    let restored = { lineItems: [], estimates: [] };
    const hasDraft = !!(draft && draft.docNumber);
    if (draft && draft.docNumber) {
      setSelectedCustomer(draft.selectedCustomer || null);
      // Restore the document's tax-exempt toggle (falls back to the customer's flag
      // for older drafts). The inherit effect is gated off during init, so this
      // restored value — including a manual toggle — is not overwritten.
      setDocTaxExempt(draft.docTaxExempt ?? !!draft.selectedCustomer?.taxExempt);
      restored = readEstimates({ estimates: draft.estimates, estimatorSpecs: draft.estimatorSpecs, lineItems: draft.lineItems || [] });
      setLineItems(restored.lineItems);
      setEstimates(restored.estimates);
      level = draft.priceLevel || 'A';
      setPriceLevel(level);
      setNotes(draft.notes || '');
      setPoNumber(draft.poNumber || '');
      setIsDelivery(!!draft.isDelivery);
      setDeliveryFee(draft.deliveryFee || '');
      setDiscountPercent(draft.discountPercent ?? 0);
      setDiscountEnabled(draft.discountEnabled ?? ((draft.discountPercent ?? 0) > 0));
      setDeliveryStreet(draft.deliveryStreet || '');
      setDeliveryCity(draft.deliveryCity || '');
      setDeliveryState(draft.deliveryState || '');
      setDeliveryZip(draft.deliveryZip || '');
      setDocNumber(draft.docNumber);
      if (docType === 'invoice') {
        setPayments(draft.payments || []);
        setBoardStatus(draft.boardStatus || 'Ordered');
      }
    } else {
      generateDocNumber();
    }

    // Apply an estimator's return through the ONE shared import in
    // utils/estimates.js. The draft saved before the trip says whether it was to
    // add a new estimate or modify an existing one. Lines arriving without
    // settings (the standalone results page) are appended unattached.
    appliedImportKeysRef.current = (hasDraft && draft.appliedImportKeys) || [];
    if (fromEstimator) {
      const importKey = estimatorImportKey(location.state.lineItems);
      if (!(importKey && appliedImportKeysRef.current.includes(importKey))) {
        const result = applyEstimatorImport({
          lineItems: restored.lineItems,
          estimates: restored.estimates,
          incomingItems: location.state.lineItems,
          incomingSpecs: location.state?.estimatorSpecs || null,
          pending: hasDraft ? draft.pendingEstimate : null,
          products,
          // The document's price level: passed by the estimator, falling back to
          // the saved draft (both originate from this document).
          priceLevel: location.state?.priceLevel || level,
          priceForLevel: getPriceForLevel,
        });
        setLineItems(result.lineItems);
        setEstimates(result.estimates);
        if (importKey) appliedImportKeysRef.current = [...appliedImportKeysRef.current, importKey];
      }
    }

    // Enable customer-change inheritance only AFTER this init settles (one tick),
    // so the null→restored-customer transition above doesn't overwrite the seeded
    // docTaxExempt. Mirrors the existing mounted-ref settle pattern.
    setTimeout(() => { canInheritTaxExemptRef.current = true; }, 0);
  }, [products]);

  // Inherit the customer's taxExempt AND default price level onto the document
  // whenever the selected customer actually CHANGES (fresh inheritance). Gated by
  // canInheritTaxExemptRef so initial mount / draft restore (which seed these
  // explicitly) win; a manual toggle/level change survives because it doesn't
  // change the customer id. Price level uses handlePriceLevelChange so line items
  // re-price to the inherited level (manual/custom/override lines are preserved).
  useEffect(() => {
    const id = selectedCustomer?.id;
    if (!canInheritTaxExemptRef.current) {
      prevCustIdRef.current = id;   // track during init, do not inherit
      return;
    }
    if (prevCustIdRef.current !== id) {
      prevCustIdRef.current = id;
      setDocTaxExempt(!!selectedCustomer?.taxExempt);
      handlePriceLevelChange(selectedCustomer?.defaultPriceLevel || 'A');
    }
  }, [selectedCustomer]);

  // `opts.pendingEstimate` records what an estimator trip is for — see
  // utils/estimates.js. Anything else (or nothing) is an add, which can never
  // remove lines.
  const saveCurrentDraft = (opts) => {
    saveDraft(docType, {
      selectedCustomer, docTaxExempt, lineItems, estimates,
      pendingEstimate: (opts && opts.pendingEstimate) || PENDING_ADD,
      appliedImportKeys: appliedImportKeysRef.current,
      priceLevel, notes, poNumber, isDelivery,
      deliveryFee, discountPercent, discountEnabled, deliveryStreet, deliveryCity, deliveryState, deliveryZip,
      docNumber,
      ...(docType === 'invoice' ? { payments, boardStatus } : {}),
    });
  };

  // Close product dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.product-search-container')) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Track unsaved changes — skip first render
  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    setHasUnsavedChanges(true);
  }, [selectedCustomer, lineItems, priceLevel, deliveryFee, discountPercent, discountEnabled, isDelivery,
      deliveryStreet, deliveryCity, deliveryState, deliveryZip, notes, poNumber, payments, boardStatus]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && lineItems.length > 0) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, lineItems]);

  const generateDocNumber = async () => {
    setDocNumber(await reserveDocNumber(docType));
  };

  // ── Quick-create customer (without leaving the document) ──
  const openNewCustomerModal = () => {
    // Pre-fill the name with whatever the user was searching, for convenience.
    setNewCustomerData({
      name: customerSearch.trim(), phone: '', email: '', address: '', notes: '', taxExempt: false,
    });
    setShowNewCustomerModal(true);
  };

  // ── Duplicate detection ──
  const normalize = (str) => (str || '').toLowerCase().trim().replace(/\s+/g, ' ');
  const normalizePhone = (phone) => (phone || '').replace(/\D/g, '');

  const findPossibleDuplicates = (newData) => {
    const newName = normalize(newData.name);
    const newPhone = normalizePhone(newData.phone);
    const newEmail = normalize(newData.email);
    if (!newName) return [];

    const matches = [];
    customers.forEach(existing => {
      const existingName = normalize(existing.name);
      // ALL of this customer's numbers, digits-only — a duplicate is a duplicate
      // whether the number matches their primary or any secondary. Still EXACT
      // equality per number (not substring): a partial number must not collapse
      // unrelated customers into a false duplicate.
      const existingPhones = getAllPhoneNumbers(existing).map(normalizePhone).filter(Boolean);
      const phoneMatches = !!newPhone && existingPhones.includes(newPhone);
      const existingEmail = normalize(existing.email);

      let matchScore = 0;
      let matchType = null;

      if (existingName === newName) {
        matchScore = 100;
        matchType = 'exact-name';
      } else if (newPhone || newEmail) {
        // Similar name only counts when phone or email also cross-matches.
        const words1 = new Set(existingName.split(' ').filter(w => w.length > 1));
        const words2 = new Set(newName.split(' ').filter(w => w.length > 1));
        let shared = 0;
        words2.forEach(w => { if (words1.has(w)) shared++; });
        const minWords = Math.min(words1.size, words2.size);
        const sharedRatio = minWords > 0 ? shared / minWords : 0;
        if (sharedRatio >= 0.5 && shared >= 1) {
          if (phoneMatches) {
            matchScore = 90;
            matchType = 'similar-name-same-phone';
          } else if (newEmail && existingEmail && newEmail === existingEmail) {
            matchScore = 85;
            matchType = 'similar-name-same-email';
          }
        }
      }

      // Same phone, different name — possibly the same person.
      if (matchScore === 0 && phoneMatches) {
        matchScore = 70;
        matchType = 'same-phone';
      }

      if (matchScore > 0) matches.push({ ...existing, matchScore, matchType });
    });

    return matches.sort((a, b) => b.matchScore - a.matchScore).slice(0, 5);
  };

  const resetNewCustomerFlow = () => {
    setShowNewCustomerModal(false);
    setShowDuplicateWarning(false);
    setPendingCustomerData(null);
    setPossibleDuplicates([]);
    setNewCustomerData({ name: '', phone: '', email: '', address: '', notes: '', taxExempt: false });
  };

  const handleCreateAndSelectCustomer = async () => {
    if (!newCustomerData.name.trim()) { alert('Customer name is required.'); return; }
    const duplicates = findPossibleDuplicates(newCustomerData);
    if (duplicates.length > 0) {
      setPossibleDuplicates(duplicates);
      setPendingCustomerData({ ...newCustomerData });
      setShowDuplicateWarning(true);
      return; // wait for the user to decide
    }
    await createCustomer(newCustomerData);
  };

  // Actual creation, extracted so the duplicate modal can call it too.
  const createCustomer = async (customerData) => {
    setCreatingCustomer(true);
    try {
      const customerNumber = await reserveCustomerNumber(customers);
      const customerToCreate = {
        name: customerData.name.trim(),
        phone: customerData.phone.trim(),
        email: customerData.email.trim(),
        address: customerData.address.trim(),
        notes: customerData.notes.trim(),
        taxExempt: customerData.taxExempt,
        customerNumber,
        createdAt: new Date().toISOString(),
      };
      const docRef = await addDoc(collection(db, 'customers'), customerToCreate);
      const newCustomer = { id: docRef.id, ...customerToCreate };
      addCustomerToCache(newCustomer);
      // Auto-select for this document — without touching any other state, so all
      // line items, price level, delivery, notes, etc. are preserved.
      setSelectedCustomer(newCustomer);
      setCustomerSearch('');
      setHasUnsavedChanges(true);
      resetNewCustomerFlow();
    } catch (err) {
      console.error('Error creating customer:', err);
      alert('Error creating customer: ' + err.message);
    }
    setCreatingCustomer(false);
  };

  const selectExistingCustomer = (existingCustomer) => {
    setSelectedCustomer(existingCustomer);
    setCustomerSearch('');
    setHasUnsavedChanges(true);
    resetNewCustomerFlow();
  };

  const createAnyway = async () => {
    if (pendingCustomerData) await createCustomer(pendingCustomerData);
  };

  // ── Product helpers ──
  const getPriceForLevel = (product, level) => {
    if (level === 'A') return product.priceA || 0;
    if (level === 'B') return product.priceB || 0;
    if (level === 'C') return product.priceC || 0;
    return product.priceA || 0;
  };

  // Small inventory badge for product rows (🔴 OUT / 🟡 LOW). Returns null when fine.
  const stockBadge = (product) => {
    if (!product?.trackInventory) return null;
    const stock = parseFloat(product.stockQty) || 0;
    const reorder = parseFloat(product.reorderPoint) || 0;
    const base = { padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, marginLeft: 8, whiteSpace: 'nowrap' };
    if (stock <= 0) return <span style={{ ...base, background: `${C_RED}26`, color: C_RED_BRIGHT }}>🔴 OUT</span>;
    if (stock <= reorder) return <span style={{ ...base, background: `${C_ORANGE}26`, color: C_ORANGE_BRIGHT }}>🟡 LOW ({stock})</span>;
    return null;
  };

  const addLineItem = (product) => {
    const status = getStockStatus(product, 1);
    if (status) {
      setStockAlertProduct(product);
      setStockAlertStatus(status);
      setShowProductDropdown(false);
      return;
    }
    commitLineItem(product);
  };

  const commitLineItem = (product) => {
    setLineItems(prev => [...prev, {
      id: Date.now() + Math.random(),
      sku: product.sku,
      description: product.description,
      unit: product.unit,
      qty: 1,
      unitPrice: getPriceForLevel(product, priceLevel),
      isStock: product.isStock,
      productId: product.id,
      fromEstimator: false,
    }]);
    setHasUnsavedChanges(true);
    setProductSearch('');
    setShowProductDropdown(false);
  };

  const updateLineItem = (id, field, value) => {
    setLineItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      // Through the shared helper: on an ON PLATE line the typed description and
      // price are the DISPLAYED ones, so the base values behind them are kept in
      // step. On every other line this is the plain field set it always was.
      const updated = editLineField(item, field, value);
      // Mark items as manually-priced so price-level changes don't overwrite the user's value.
      if (field === 'unitPrice') updated.manualPrice = true;
      return updated;
    }));
  };

  // ── ON PLATE ──
  // Appends " ON PLATE" to the description and adds $50 to the UNIT price (so
  // the quantity multiplies it). Unticking restores the exact original text and
  // price — see utils/onPlate.js. Nothing here touches inventory: a plate is not
  // deducted as a product.
  const toggleOnPlate = (id, checked) => {
    setLineItems(prev => prev.map(item => (item.id === id ? setLineOnPlate(item, checked) : item)));
    setHasUnsavedChanges(true);
  };

  const addProductsFromPicker = (selectedProducts) => {
    if (!selectedProducts || selectedProducts.length === 0) return;
    const withAlerts = selectedProducts
      .map(p => ({ product: p, status: getStockStatus(p, 1) }))
      .filter(item => item.status !== null);
    if (withAlerts.length > 0) {
      // Surface a consolidated alert over the still-open picker; remember the
      // full selection so "Add All Anyway" adds OK items too.
      setBatchStockAlerts(withAlerts);
      setPendingBatchProducts(selectedProducts);
      return;
    }
    commitProductsFromPicker(selectedProducts);
  };

  const commitProductsFromPicker = (selectedProducts) => {
    if (!selectedProducts || selectedProducts.length === 0) return;
    const baseId = Date.now();
    const newItems = selectedProducts.map((product, index) => ({
      id: baseId + index + Math.random(),
      sku: product.sku,
      description: product.description,
      unit: product.unit || 'pc',
      qty: 1,
      unitPrice: getPriceForLevel(product, priceLevel),
      isStock: product.isStock ?? true,
      productId: product.id,
      fromEstimator: false,
    }));
    setLineItems(prev => [...prev, ...newItems]);
    setHasUnsavedChanges(true);
    setShowProductPicker(false);
    setBatchStockAlerts([]);
    setPendingBatchProducts(null);
  };

  const resetItemPrice = (id) => {
    setLineItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const product = products.find(p => p.id === item.productId) ||
        (item.sku && item.sku !== '—' && item.sku !== 'NON-STOCK' && item.sku !== 'TEMPLATE'
          ? products.find(p => p.sku === item.sku && p.isActive)
          : null);
      if (!product) return { ...item, manualPrice: false, itemPriceLevel: null };
      // setLineBasePrice, not a raw unitPrice write: on an ON PLATE line the
      // $50 is re-applied on top of the price-level price, so resetting can
      // never leave the line reading ON PLATE without the surcharge.
      return { ...setLineBasePrice(item, getPriceForLevel(product, priceLevel)), manualPrice: false, itemPriceLevel: null };
    }));
    setHasUnsavedChanges(true);
  };

  const openItemEditor = (item) => {
    if (item?.isTextNote) return; // text notes have no qty/price to edit
    setEditingItem(item);
    setShowItemEditor(true);
  };

  const saveItemEdits = (updatedItem) => {
    // The modal edits the DISPLAYED description and price and knows nothing
    // about plates, so the bases are re-derived from what it returns.
    setLineItems(prev => prev.map(item => item.id === updatedItem.id ? reconcileEditedLine({ ...updatedItem }) : item));
    setHasUnsavedChanges(true);
    setShowItemEditor(false);
    setEditingItem(null);
  };

  const removeLineItem = (id) => setLineItems(prev => prev.filter(item => item.id !== id));

  // Move the selected line item up/down by swapping array positions. Selection is
  // tracked by id, so the highlight follows the item and ▲/▼ can be clicked repeatedly.
  const moveLineItem = (direction) => {
    setLineItems(prev => {
      const idx = prev.findIndex(it => it.id === selectedLineId);
      if (idx === -1) return prev;
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
    setHasUnsavedChanges(true);
  };

  const addTemplateBundleToDocument = (template) => {
    const items = template.items || [];
    if (items.length === 0) {
      alert('This template has no items.');
      return;
    }
    const bundleId = `bundle-${Date.now()}`;
    const baseId = Date.now();
    const newItems = items.map((item, index) => ({
      id: baseId + index + Math.random(),
      sku: 'TEMPLATE',
      description: item.description,
      unit: item.unit || 'pc',
      qty: parseFloat(item.defaultQty) || 1,
      unitPrice: parseFloat(item.defaultPrice) || 0,
      costPrice: parseFloat(item.defaultCost) || 0,
      isStock: false,
      productId: null,
      fromTemplate: true,
      templateBundleId: bundleId,
      templateBundleName: template.name,
      templateBundleOrder: index,
      fromEstimator: false,
    }));
    setLineItems(prev => [...prev, ...newItems]);
    setHasUnsavedChanges(true);
    setShowTemplatesPicker(false);
  };

  const addStockBundleToDocument = (bundle) => {
    const items = bundle.items || [];
    if (items.length === 0) {
      alert('This bundle has no items.');
      return;
    }
    const bundleId = `stockbundle-${Date.now()}`;
    const baseId = Date.now();
    const newItems = items.map((bundleItem, index) => {
      const product = products.find(p => p.id === bundleItem.productId);
      if (!product) {
        return {
          id: baseId + index + Math.random(),
          sku: bundleItem.sku || '—',
          description: `⚠️ NOT FOUND: ${bundleItem.description}`,
          unit: 'pc', qty: 1, unitPrice: 0, costPrice: 0,
          isStock: false, productId: null,
          fromStockBundle: true, stockBundleId: bundleId,
          stockBundleName: bundle.name, stockBundleOrder: index,
          fromEstimator: false,
        };
      }
      return {
        id: baseId + index + Math.random(),
        sku: product.sku,
        description: product.description,
        unit: product.unit || 'pc',
        qty: 1,
        unitPrice: getPriceForLevel(product, priceLevel),
        costPrice: parseFloat(product.costPrice) || 0,
        isStock: product.isStock ?? true,
        productId: product.id,
        fromStockBundle: true,
        stockBundleId: bundleId,
        stockBundleName: bundle.name,
        stockBundleOrder: index,
        fromEstimator: false,
      };
    });
    setLineItems(prev => [...prev, ...newItems]);
    setHasUnsavedChanges(true);
    setShowTemplatesPicker(false);
  };

  const confirmNonStockItem = () => {
    if (!nonStockForm.description.trim()) { alert('Please enter a description.'); return; }
    setLineItems(prev => [...prev, {
      id: Date.now(),
      sku: 'NON-STOCK',
      description: nonStockForm.description.trim(),
      unit: nonStockForm.unit,
      qty: parseFloat(nonStockForm.qty) || 1,
      unitPrice: parseFloat(nonStockForm.unitPrice) || 0,
      cost: parseFloat(nonStockForm.cost) || 0,
      isStock: false,
      isNonStockCustom: true,
      productId: null,
      fromEstimator: false,
    }]);
    setShowNonStockForm(false);
    setNonStockForm({ description: '', qty: 1, unit: 'pc', unitPrice: '', cost: '' });
  };

  const handlePriceLevelChange = (newLevel) => {
    setPriceLevel(newLevel);
    setHasUnsavedChanges(true);
    setLineItems(prev => prev.map(item => {
      if (item.isTextNote) return item;
      if (item.isNonStockCustom) return item;
      if (item.manualPrice) return item;
      if (item.itemPriceLevel) return item;
      // Through setLineBasePrice for the same reason as resetItemPrice above.
      if (item.productId) {
        const product = products.find(p => p.id === item.productId);
        if (product) return setLineBasePrice(item, getPriceForLevel(product, newLevel));
      }
      if (item.sku && item.sku !== '—' && item.sku !== 'NON-STOCK') {
        const product = products.find(p => p.sku === item.sku && p.isActive);
        if (product) return { ...setLineBasePrice(item, getPriceForLevel(product, newLevel)), productId: product.id };
      }
      return item;
    }));
  };

  // ── Computed totals ──
  // All of these come back already rounded to cents from the shared helper, so
  // the figures shown here, the figures printed, and the figures written to
  // Firestore are the same numbers — and the printed line totals sum exactly to
  // the subtotal. Business rules (percentage discount off the subtotal, tax on
  // the POST-discount base, delivery never discounted and never taxed,
  // tax-exempt charged no tax) are unchanged; see computeDocTotals.
  const { subtotal, discountAmount, taxableBase, delivery, tax, grandTotal } =
    computeDocTotals(lineItems, {
      discountPercent,
      deliveryFee,
      taxExempt: docTaxExempt,
      taxRate: TAX_RATE,
    });

  // Reorder button state — based on the selected row's position in the array.
  const selectedLineIndex = lineItems.findIndex(it => it.id === selectedLineId);
  const canMoveLineUp = selectedLineIndex > 0;
  const canMoveLineDown = selectedLineIndex >= 0 && selectedLineIndex < lineItems.length - 1;

  // Phone matches on DIGITS ONLY, across EVERY number the customer has (phones
  // array + legacy field). Skipped when the term has no digits, so a name search
  // behaves exactly as it did before. `normalizePhone` is the same digits helper
  // the duplicate detection above uses.
  const customerSearchDigits = normalizePhone(customerSearch);
  // The customer's LIVE record, for the credit-hold warning: a hold is a live
  // status, so it is read from the customers cache rather than from anything
  // stored on the document. Falls back to the selected object for a customer
  // just created here, which is not in the cache under a different shape.
  const liveCustomer = customers.find(c => c.id === selectedCustomer?.id) || selectedCustomer;

  const filteredCustomers = customers.filter(c =>
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    (customerSearchDigits.length > 0 &&
      getAllPhoneNumbers(c).some(n => normalizePhone(n).includes(customerSearchDigits))) ||
    c.customerNumber?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const filteredProducts = products.filter(p => {
    const matchSearch = !productSearch ||
      p.description?.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.sku?.toLowerCase().includes(productSearch.toLowerCase());
    const matchCategory = filterCategory === 'All' || p.category === filterCategory;
    return p.isActive && matchSearch && matchCategory;
  });

  // Status of the payments STAGED on this form, from the shared engine. None of
  // them is in the payments collection yet; they are written when the invoice is.
  const payStatus = getPaymentStatus(grandTotal, payments);
  const { totalPaid, balanceDue, creditAmount } = payStatus;

  // Cash "change due" math — mirrors ViewDocument.js. `balanceDue` above is already
  // the cent-rounded remaining balance (grandTotal on a fresh invoice; net of any
  // payments already staged here), so it's the correct base for each new payment.
  const isCash = currentPayment.method === 'Cash';
  const cashReceived = parseFloat(currentPayment.cashReceived) || 0;
  // Never record more than the balance: the excess is physical change, not a payment.
  const recordedAmount = isCash ? round2(Math.min(cashReceived, balanceDue)) : round2(parseFloat(currentPayment.amount) || 0);
  // Display/informational only — never counts toward totalPaid.
  const changeDue = isCash ? Math.max(0, round2(cashReceived - balanceDue)) : 0;

  // The cost-source priority now lives in utils/lineMath.js (resolveUnitCost),
  // so this reducer, the other document editor and the line item editor cannot
  // disagree about what a line costs. Same priority, same lookup semantics —
  // the numbers are unchanged.
  const totalCost = lineItems.reduce(
    (sum, item) => sum + lineCost(item, resolveUnitCost(item, costLookup)), 0);
  // Profit is on the pre-tax sale of GOODS after discount — excludes delivery and tax.
  const grossProfit = taxableBase - totalCost;
  const profitMarginPct = taxableBase > 0 ? (grossProfit / taxableBase) * 100 : 0;

  const buildDocData = () => ({
    docNumber, docType,
    customerId: selectedCustomer.id,
    customerName: selectedCustomer.name,
    customerAddress: selectedCustomer.address,
    customerNumber: selectedCustomer.customerNumber,
    customerPhone: selectedCustomer.phone || '',
    taxExempt: docTaxExempt,
    // Snapshot account status so invoice-type docs can label "INVOICE" (account)
    // vs "SALE" (regular) without a live customer lookup. Stored on quotes too so
    // it carries through quote→invoice conversion.
    customerAccount: !!selectedCustomer.isAccountCustomer,
    salesperson: userName,
    priceLevel, lineItems,
    subtotal, deliveryFee: delivery, tax, grandTotal,
    discountPercent: parseFloat(discountPercent) || 0, discountAmount,
    notes, poNumber: poNumber.trim(), isDelivery,
    deliveryStreet: isDelivery ? deliveryStreet : '',
    deliveryCity: isDelivery ? deliveryCity : '',
    deliveryState: isDelivery ? deliveryState : '',
    deliveryZip: isDelivery ? deliveryZip : '',
    // Does this document carry estimator lines? Written the same way by both
    // pages. It used to mean "was this page opened straight from an estimator",
    // which EditDocument never wrote at all — nothing reads it, but a stored
    // flag should at least say something true.
    fromEstimator: lineItems.some(item => item && item.fromEstimator),
    // Every estimate; each line's estimateId travels inside lineItems.
    estimates,
    // ⚠️ DERIVED MIRROR of the most recent estimate's settings, kept during the
    // transition for anything still reading the old single field. `estimates`
    // is the record — never read this back as truth.
    estimatorSpecs: estimatorSpecsMirror(estimates),
    ...(docType === 'invoice' && {
      payments,
      // A DERIVED MIRROR, never truth (see utils/paymentStatus.js). This is the
      // write that CREATES the invoice, so the payments collection holds nothing
      // for it yet: the mirror is derived from exactly that. The staged payments
      // are written as records next, and the mirror is rebuilt from the
      // collection after them (rebuildMirrorAfterCreate).
      ...paymentMirrorFields(grandTotal, []),
      status: boardStatus,
      statusUpdatedAt: new Date().toISOString(),
    }),
  });

  const saveAllPaymentRecords = async (savedInvoiceId, invoiceNumber) => {
    if (payments.length === 0) return;
    await Promise.all(payments.map(p =>
      addDoc(collection(db, 'payments'), {
        invoiceId: savedInvoiceId,
        invoiceNumber,
        customerId: selectedCustomer?.id,
        customerName: selectedCustomer?.name,
        amount: parseFloat(p.amount) || 0,
        method: p.method,
        // Informational only — `amount` above (the capped balance) is what counts
        // toward totalPaid. Field names match ViewDocument.js so the Payment
        // History's compact "(Cash $X · Change $Y)" note renders for these too.
        cashReceived: p.cashReceived != null ? parseFloat(p.cashReceived) : null,
        changeDue: p.changeDue != null ? parseFloat(p.changeDue) : null,
        checkNumber: p.method === 'Check' ? p.checkNumber : '',
        date: p.date,
        notes: p.notes,
        recordedBy: userName,
        createdAt: new Date().toISOString(),
        // A label on the record ("the last payment, which settled it") — not the
        // invoice's status, and read by nothing.
        isFullPayment: isSettled(payStatus.status) && payments.indexOf(p) === payments.length - 1,
      })
    ));
  };

  // After a NEW invoice's payment records are written, rebuild its mirror from
  // the collection. Skipped when none were staged: the collection then holds
  // nothing for the new id, which is exactly what the creating write reflected.
  //
  // A failure here does NOT fail the save. The invoice and its payments exist;
  // "Error saving. Please try again." would create a second invoice with a
  // second set of payments. The user is told what happened instead.
  const rebuildMirrorAfterCreate = async (invoiceId) => {
    if (payments.length === 0) return;
    try {
      await syncInvoicePaymentMirror(invoiceId);
    } catch (err) {
      console.error('Invoice saved, but its payment mirror could not be rebuilt:', err);
      alert(mirrorSyncFailedMessage('The invoice and its payments WERE saved. Do not save it again.'));
    }
  };


  // Re-open ONE estimate's estimator with ITS settings, as a modify of THAT
  // estimate. The draft records which one, so the return replaces its lines and
  // no one else's (see utils/estimates.js).
  const handleModifyEstimator = (estimate) => {
    if (!estimate) return;
    const confirmed = window.confirm(
      `Going back to the estimator will let you modify all settings for:\n\n${estimate.label}\n\nIf you click Calculate Materials, this estimate's items will be replaced with the new calculation. Other estimates and all other lines are not touched.\n\nIf you go back without recalculating, nothing changes.\n\nContinue?`
    );
    if (!confirmed) return;
    saveCurrentDraft({ pendingEstimate: pendingModify(estimate) });
    const estimatorRoute = estimate.kind === 'vinyl' ? '/vinyl-estimator'
      : estimate.kind === 'wood' ? '/wood-estimator'
      : estimate.kind === 'aluminum' ? '/aluminum-estimator'
      : '/estimator';
    navigate(estimatorRoute, {
      state: {
        returnTo: docType === 'quote' ? '/new-quote' : '/new-invoice',
        previousSpecs: estimate.specs,
        priceLevel,
      },
    });
  };

  // Remove ONE estimate: its entry and every line carrying its id. The panel has
  // already confirmed, naming the estimate and its line count.
  const handleRemoveEstimate = (estimate) => {
    if (!estimate) return;
    const next = removeEstimate({ lineItems, estimates }, estimate.id);
    setLineItems(next.lineItems);
    setEstimates(next.estimates);
    setHasUnsavedChanges(true);
  };

  const handleAddFromEstimator = () => {
    saveCurrentDraft();
    navigate('/estimator', {
      state: {
        returnTo: docType === 'quote' ? '/new-quote' : '/new-invoice',
        priceLevel,
      },
    });
  };

  const handleAddFromVinylEstimator = () => {
    saveCurrentDraft();
    navigate('/vinyl-estimator', {
      state: {
        returnTo: docType === 'quote' ? '/new-quote' : '/new-invoice',
        priceLevel,
      },
    });
  };

  const handleAddFromWoodEstimator = () => {
    saveCurrentDraft();
    navigate('/wood-estimator', {
      state: {
        returnTo: docType === 'quote' ? '/new-quote' : '/new-invoice',
        priceLevel,
      },
    });
  };

  const handleAddFromAluminumEstimator = () => {
    saveCurrentDraft();
    navigate('/aluminum-estimator', {
      state: {
        returnTo: docType === 'quote' ? '/new-quote' : '/new-invoice',
        priceLevel,
      },
    });
  };

  const handleSave = async () => {
    if (!selectedCustomer) { alert('⚠️ Please select a customer before saving.'); return; }
    if (lineItems.length === 0) { alert('⚠️ Please add at least one item before saving.'); return; }
    setSaving(true);
    try {
      const col = docType === 'quote' ? 'quotes' : 'invoices';
      const ref = await addDoc(collection(db, col), {
        ...buildDocData(),
        date: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        // Manual inventory model: a new invoice starts pending — no stock moved
        // here and no modal. The user deducts later via Mark-as-Deducted.
        ...(docType === 'invoice' ? { inventoryStatus: 'pending' } : {}),
      });
      if (docType === 'invoice') {
        await saveAllPaymentRecords(ref.id, docNumber);
        await rebuildMirrorAfterCreate(ref.id);
      }
      setHasUnsavedChanges(false);
      setSaved(true);
      clearDraft(docType);
      setTimeout(() => navigate(`/view-document/${docType}/${ref.id}`, { state: { autoPrint: true } }), 1500);
    } catch (err) {
      console.error('Error saving:', err);
      alert('Error saving. Please try again.');
    }
    setSaving(false);
  };

  const handleSaveAndConvert = async () => {
    if (!selectedCustomer) { alert('⚠️ Please select a customer before saving.'); return; }
    if (lineItems.length === 0) { alert('⚠️ Please add at least one item before saving.'); return; }
    setSavingAndConverting(true);
    try {
      const quoteData = {
        ...buildDocData(),
        docType: 'quote',
        date: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      };

      const quoteDocRef = await addDoc(collection(db, 'quotes'), quoteData);

      const invoiceNumber = await reserveDocNumber('invoice');
      const now = new Date().toISOString();

      const invoiceRef = await addDoc(collection(db, 'invoices'), {
        ...quoteData,
        docNumber: invoiceNumber,
        docType: 'invoice',
        convertedFromQuote: docNumber,
        createdAt: now,
        date: now,
        payments,
        // Mirror for an invoice that does not exist until this write — no records
        // in the collection yet. Rebuilt from the collection just below.
        ...paymentMirrorFields(grandTotal, []),
        status: boardStatus,
        statusUpdatedAt: now,
        // Manual inventory model: the converted invoice starts pending — no stock
        // moved here and no prompt. The user deducts later via Mark-as-Deducted.
        inventoryStatus: 'pending',
      });

      await saveAllPaymentRecords(invoiceRef.id, invoiceNumber);
      await rebuildMirrorAfterCreate(invoiceRef.id);
      await updateDoc(quoteDocRef, { convertedToInvoice: invoiceNumber });
      setHasUnsavedChanges(false);
      clearDraft(docType);

      alert(`Quote saved and converted to Invoice ${invoiceNumber}!`);
      navigate(`/view-document/invoice/${invoiceRef.id}`, { state: { autoPrint: true } });
    } catch (err) {
      console.error('Error saving and converting:', err);
      alert('Error saving. Please try again.');
    }
    setSavingAndConverting(false);
  };

  const confirmLeave = (destination) => {
    if (hasUnsavedChanges && lineItems.length > 0) {
      const confirmed = window.confirm(
        '⚠️ You have unsaved changes with items in this document. Your work will be lost if you leave without saving.\n\nDo you want to leave anyway?'
      );
      if (confirmed) {
        clearDraft(docType);
        navigate(destination);
      }
    } else {
      clearDraft(docType);
      navigate(destination);
    }
  };

  // ── Round Up Quantities ──
  // Which products round is the OWNER'S choice, made per product in Inventory
  // with the "Round up to full package on quotes" tick, and the size comes from
  // that product's own packQty. There is no table here and none in the other
  // editor either.
  //
  // THE ALERT SAYS THREE DIFFERENT THINGS, and staying quiet is the fourth:
  //   rounded        — what moved, and from what to what
  //   marked, unset  — the owner asked for rounding on a product whose record
  //                    cannot say what a package holds. Named out loud: it is a
  //                    half-finished record, and only the owner can finish it.
  //   skipped        — the line matches no product at all (template lines carry
  //                    sku 'TEMPLATE' and no productId). These were previously
  //                    neither rounded NOR reported, which is the exact silent
  //                    no-op that let barbed wire go unrounded for a year.
  //   silence        — a product with a pack size that is NOT marked to round.
  //                    That is nearly every product in the catalogue; saying so
  //                    every time would bury the two messages above.
  const roundUpQuantities = () => {
    const { items, changes, missing, skipped } = roundLineItems(lineItems, products);
    setLineItems(items);

    const lines = [];
    if (changes.length > 0) {
      lines.push(`✅ Rounded up ${changes.length} item(s):`);
      lines.push('');
      changes.forEach(c => lines.push(`${c.description}: ${c.from} → ${c.to}`));
    }
    if (missing.length > 0) {
      if (lines.length > 0) lines.push('');
      lines.push('⚠️ Marked to round up, but no pack size is set:');
      missing.forEach(m => lines.push(
        `${m.sku} — ${m.description}. Set pcs per package in Inventory.`
      ));
    }
    if (skipped.length > 0) {
      if (lines.length > 0) lines.push('');
      lines.push('ℹ️ Skipped — these lines are not linked to a product, so there is');
      lines.push('nothing to look a pack size up on:');
      skipped.forEach(s => lines.push(`${s.sku} — ${s.description}`));
    }
    alert(lines.length > 0 ? lines.join('\n') : 'No items needed rounding up.');
  };

  // ── Render ──
  return (
    <>
      <div style={{
        minHeight: '100vh',
        background: theme.bgGradient,
        padding: 20,
        color: C_TEXT,
      }}>
        <style>{`
          select.dark-select option { background: ${theme.optionBg}; color: ${C_TEXT}; }
          .product-search-container input::placeholder { color: #475569; }
        `}</style>

        <div style={{ maxWidth: 1280, margin: '0 auto' }}>

          {saved && (
            <div style={{
              background: `${C_GREEN}1A`, border: `1px solid ${C_GREEN}4D`,
              color: C_GREEN_BRIGHT, padding: '14px 20px', borderRadius: 12,
              marginBottom: 16, textAlign: 'center', fontWeight: 700,
            }}>
              ✅ {docType === 'quote' ? 'Quote' : 'Invoice'} {docNumber} saved! Redirecting...
            </div>
          )}

          {location.state?.lineItems && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              background: `${C_CYAN}14`, border: `1px solid ${C_CYAN}4D`,
              borderRadius: 12, padding: '14px 20px', marginBottom: 16,
            }}>
              <span style={{ fontSize: 24 }}>🔧</span>
              <div>
                <p style={{ color: C_CYAN_BRIGHT, fontWeight: 700, margin: 0 }}>Materials loaded from Estimator</p>
                <p style={{ color: C_TEXT_2, fontSize: 13, margin: '2px 0 0 0' }}>{location.state.lineItems.length} items imported.</p>
              </div>
            </div>
          )}

          {/* Header */}
          <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, background: '#E6F1FB', border: '1px solid #E3ECF5', borderRadius: 12, flexShrink: 0 }}>
                <img src={require('../assets/fenceflow-logo.png')} alt="Fence Flow" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 8 }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: '#03045E' }}>
                    {docType === 'quote' ? '📄 New Quote' : '🧾 New Invoice'}
                  </h1>
                  <span style={{ fontFamily: 'monospace', color: '#0077B6', fontSize: 16, fontWeight: 700, background: '#E6F1FB', padding: '4px 12px', borderRadius: 8, border: '1px solid #E3ECF5' }}>
                    #{docNumber}
                  </span>
                </div>
                <p style={{ color: '#6B7C93', fontSize: 12, margin: '4px 0 0 0', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 600 }}>
                  Salesperson: <span style={{ color: '#0077B6' }}>{userName}</span> · {today}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button onClick={handleSave} disabled={saving || saved || savingAndConverting || !selectedCustomer || lineItems.length === 0}
                style={{
                  background: '#0077B6', border: 'none', color: '#fff', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 700, transition: 'background 0.3s',
                  opacity: (saving || saved || savingAndConverting || !selectedCustomer || lineItems.length === 0) ? 0.5 : 1,
                  cursor: (saving || saved || savingAndConverting || !selectedCustomer || lineItems.length === 0) ? 'not-allowed' : 'pointer',
                }}>
                {saving ? 'Saving...' : saved ? '✅ Saved!' : `💾 Save ${docType === 'quote' ? 'Quote' : 'Invoice'}`}
              </button>
              <button onClick={() => setShowOfficeUse(true)} style={{ background: '#E6F1FB', border: '1px solid #0077B6', color: '#0077B6', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s' }} title="Office Use Only">🔒 OU</button>
              <button onClick={() => confirmLeave('/dashboard')} style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s' }}>← Dashboard</button>
            </div>
          </div>

          {/* Customer */}
          <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20, marginBottom: 16, position: 'relative', zIndex: 30, overflow: 'visible' }}>
            <label style={{ color: '#6B7C93', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>Customer <span style={{ color: '#E24B4A' }}>*</span></label>
            {/* The hold, at the moment the customer is selected and for as long
                as the document is being worked on. Read from the CUSTOMER
                record (live status), never from the document. Warns only —
                nothing here stops the document being built or saved. */}
            <CreditHoldWarning customer={liveCustomer} style={{ marginBottom: 12 }} />
            {selectedCustomer ? (
              <div style={{ background: '#E6F1FB', border: '1px solid #E3ECF5', borderRadius: 12, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0 }}>
                  <button onClick={() => navigate(`/customer-profile/${selectedCustomer.id}`)}
                    style={{ background: 'transparent', border: 'none', color: '#0077B6', fontWeight: 700, fontSize: 16, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                    {selectedCustomer.name}
                  </button>
                  <p style={{ color: '#6B7C93', fontSize: 13, margin: '4px 0 0 0' }}>{selectedCustomer.address}</p>
                  <p style={{ color: '#6B7C93', fontSize: 13, margin: '2px 0 0 0' }}>
                    <span style={{ color: '#6B7C93', fontFamily: 'monospace', fontWeight: 700 }}>{selectedCustomer.customerNumber}</span> · {selectedCustomer.phone}
                    {selectedCustomer.taxExempt && (
                      <span style={{ background: '#E6F7EE', color: '#0F6E56', padding: '2px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, marginLeft: 10, letterSpacing: 0.5 }}>TAX EXEMPT</span>
                    )}
                    {isDelivery && (
                      <span style={{ background: '#E6F1FB', color: '#0077B6', padding: '2px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700, marginLeft: 8 }}>🚚 DELIVERY ORDER</span>
                    )}
                  </p>
                </div>
                <button onClick={() => setSelectedCustomer(null)} style={{ background: '#FBECEC', border: '1px solid #E9C3C3', color: '#A32D2D', padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s' }}>✕ Change</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                <input type="text" placeholder="Search customer by name, phone, or number..."
                  value={customerSearch}
                  onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                  onFocus={(e) => { setShowCustomerDropdown(true); e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                  style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }} />
                {showCustomerDropdown && customerSearch && (
                  <div style={{
                    position: 'absolute', zIndex: 100, top: 'calc(100% + 6px)', left: 0, right: 0,
                    background: '#FFFFFF',
                    border: '1px solid #E3ECF5', borderRadius: 12,
                    maxHeight: 240, overflowY: 'auto',
                    boxShadow: '0 12px 30px rgba(3, 4, 94, 0.12)',
                  }}>
                    {filteredCustomers.length === 0 ? (
                      <div style={{ padding: '12px 16px' }}>
                        <p style={{ color: '#6B7C93', fontSize: 13, margin: 0 }}>No customers found.</p>
                        <button onClick={openNewCustomerModal} style={{ background: 'transparent', border: 'none', color: '#0077B6', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '4px 0 0 0' }}>+ Create New Customer</button>
                      </div>
                    ) : (
                      filteredCustomers.map(c => (
                        <div key={c.id}
                          onClick={() => { setSelectedCustomer(c); setShowCustomerDropdown(false); setCustomerSearch(''); }}
                          style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #E3ECF5', transition: 'background 0.2s' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#E6F1FB'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <p style={{ color: '#03045E', fontWeight: 600, fontSize: 14, margin: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>{c.name}<CreditHoldWarning customer={c} size="compact" /></p>
                          <p style={{ color: '#6B7C93', fontSize: 12, margin: '2px 0 0 0' }}>
                            <span style={{ color: '#0077B6', fontFamily: 'monospace' }}>{c.customerNumber}</span> · {c.phone}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                )}
                </div>
                <button onClick={openNewCustomerModal}
                  style={{ background: '#E6F1FB', border: '1px solid #0077B6', color: '#0077B6', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  + New Customer
                </button>
              </div>
            )}
          </div>

          {/* PO + Board Status + Price Level */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20, marginBottom: 16 }}>
              <label style={{ color: '#6B7C93', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>P.O. Number</label>
              <input type="text" value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="Optional"
                style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }}
                onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }} />
            </div>
            {docType === 'invoice' && (
              <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20, marginBottom: 16 }}>
                <label style={{ color: '#6B7C93', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>📋 Board Status</label>
                <select value={boardStatus} onChange={e => setBoardStatus(e.target.value)} className="dark-select"
                  style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', cursor: 'pointer', transition: 'border-color 0.3s, box-shadow 0.3s' }}>
                  {BOARD_STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
            )}
            <div style={{ background: '#F3F2FD', border: '2px solid #534AB7', borderRadius: 16, padding: 20, marginBottom: 16 }}>
              <label style={{ color: '#3C3489', fontSize: 11, fontWeight: 500, letterSpacing: '0.03em', textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>PRICE LEVEL</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {['A', 'B', 'C'].map(level => {
                  const isActive = priceLevel === level;
                  return (
                    <button key={level} onClick={() => handlePriceLevelChange(level)}
                      style={{
                        flex: 1, padding: 10,
                        background: isActive ? '#534AB7' : '#FFFFFF',
                        border: `1px solid ${isActive ? '#534AB7' : '#CECBF6'}`,
                        borderRadius: 10,
                        color: isActive ? '#FFFFFF' : '#534AB7',
                        fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        transition: 'background 0.3s, border-color 0.3s, color 0.3s',
                      }}>
                      {level}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20, marginBottom: 16, position: 'relative', zIndex: 20, overflow: 'visible' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <h3 style={{ color: '#03045E', fontWeight: 700, fontSize: 16, margin: 0, letterSpacing: 0.5 }}>
                  📋 Line Items
                  {lineItems.filter(i => i.fromEstimator).length > 0 && (
                    <span style={{ marginLeft: 10, background: '#E6F1FB', color: '#0077B6', padding: '2px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>
                      🔧 {lineItems.filter(i => i.fromEstimator).length} from Estimator
                    </span>
                  )}
                </h3>
                {lineItems.length > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} title={selectedLineId ? '' : 'Select a line item first'}>
                    <button type="button" onClick={() => moveLineItem('up')} disabled={!canMoveLineUp} title="Move up"
                      style={{ background: canMoveLineUp ? '#E6F1FB' : '#CDE7F5', border: '1px solid #E3ECF5', color: canMoveLineUp ? '#0077B6' : '#C3CBD6', width: 32, height: 32, borderRadius: 8, fontSize: 13, fontWeight: 700, lineHeight: 1, cursor: canMoveLineUp ? 'pointer' : 'default' }}>▲</button>
                    <button type="button" onClick={() => moveLineItem('down')} disabled={!canMoveLineDown} title="Move down"
                      style={{ background: canMoveLineDown ? '#E6F1FB' : '#CDE7F5', border: '1px solid #E3ECF5', color: canMoveLineDown ? '#0077B6' : '#C3CBD6', width: 32, height: 32, borderRadius: 8, fontSize: 13, fontWeight: 700, lineHeight: 1, cursor: canMoveLineDown ? 'pointer' : 'default' }}>▼</button>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {/* All four estimators collapsed into one dropdown. The handlers
                    themselves are unchanged — the menu just calls them. */}
                <EstimatorMenu
                  onChainLink={handleAddFromEstimator}
                  onVinyl={handleAddFromVinylEstimator}
                  onWood={handleAddFromWoodEstimator}
                  onAluminum={handleAddFromAluminumEstimator}
                />
                {/* One estimate: a single "Modify <label>" button here, where the
                    old Modify button was. Two or more: EstimatesList below. */}
                <EstimateModifyControl
                  estimates={estimates}
                  lineItems={lineItems}
                  onModify={handleModifyEstimator}
                  onRemove={handleRemoveEstimate}
                />
                <button onClick={() => setShowTemplatesPicker(true)}
                  style={{ background: '#E4F3FA', border: '1px solid #0096C7', color: '#0096C7', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}>📋 Templates</button>
                <button onClick={() => { setShowNonStockForm(true); setShowProductDropdown(false); }}
                  style={{ background: '#E0F7FA', border: '1px solid #00B4D8', color: '#0096C7', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}>+ Non-Stock</button>
                <button onClick={() => { setLineItems(prev => [...prev, { id: Date.now(), isTextNote: true, description: '' }]); setHasUnsavedChanges(true); }}
                  style={{ background: '#EEF2F7', border: '1px solid #9AA8BC', color: '#4A5A70', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}>+ Text/Note</button>
                <button onClick={roundUpQuantities}
                  style={{ background: '#EAF8FC', border: '1px solid #48CAE4', color: '#0096C7', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                  title="Rounds each line up to a whole package, using the pack size on the product">🔄 Round Up</button>
              </div>
            </div>

            {/* Two or more estimates: one row each. Also the note about estimator
                lines from older documents that belong to no estimate. */}
            <EstimatesList
              estimates={estimates}
              lineItems={lineItems}
              onModify={handleModifyEstimator}
              onRemove={handleRemoveEstimate}
            />

            <div className="product-search-container" style={{ position: 'relative' }}>
              <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => setShowProductPicker(true)}
                  style={{ background: '#0077B6', border: 'none', color: '#fff', padding: '10px 18px', borderRadius: 12, fontSize: 13, fontWeight: 700, letterSpacing: 0.5, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,119,182,0.3)', transition: 'transform 0.3s, box-shadow 0.3s', flex: '0 0 auto' }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,119,182,0.45)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,119,182,0.3)';
                  }}
                >
                  🛒 Add Products
                </button>
                <div style={{ flex: 1, position: 'relative', minWidth: 260 }}>
                  <input type="text" placeholder="Quick search by description or SKU..."
                    value={productSearch}
                    onChange={(e) => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                    onFocus={(e) => { setShowProductDropdown(true); e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                    style={{ width: '100%', padding: '10px 14px', paddingLeft: 44, background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }} />
                  <span style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#6B7C93', fontSize: 16, pointerEvents: 'none' }}>🔍</span>
                </div>
              </div>

              {showProductDropdown && (productSearch || filterCategory !== 'All') && (
                <div style={{
                  position: 'absolute', zIndex: 100, top: 'calc(100% - 4px)', left: 0, right: 0,
                  background: '#FFFFFF',
                  border: '1px solid #E3ECF5', borderRadius: 12,
                  maxHeight: 280, overflowY: 'auto',
                  boxShadow: '0 12px 30px rgba(3, 4, 94, 0.12)',
                }}>
                  {filteredProducts.length === 0 ? (
                    <p style={{ padding: '12px 16px', color: '#6B7C93', margin: 0 }}>No products found.</p>
                  ) : (
                    filteredProducts.slice(0, 50).map(p => (
                      <div key={p.id} onMouseDown={() => addLineItem(p)}
                        style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #E3ECF5', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'background 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#E6F1FB'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <div style={{ minWidth: 0 }}>
                          <p style={{ color: '#03045E', fontWeight: 600, fontSize: 13, margin: 0 }}>{p.description}{stockBadge(p)}</p>
                          <p style={{ color: '#6B7C93', fontSize: 11, margin: '2px 0 0 0' }}>
                            <span style={{ color: '#0077B6', fontFamily: 'monospace', fontWeight: 700 }}>{p.sku}</span>
                            {' · '}{p.unit} · {p.category}
                          </p>
                        </div>
                        <div style={{ textAlign: 'right', marginLeft: 16, flexShrink: 0 }}>
                          <p style={{ color: priceLevel === 'A' ? '#0077B6' : '#6B7C93', fontSize: 11, fontWeight: priceLevel === 'A' ? 700 : 500, margin: 0 }}>A: ${parseFloat(p.priceA || 0).toFixed(2)}</p>
                          <p style={{ color: priceLevel === 'B' ? '#0077B6' : '#6B7C93', fontSize: 11, fontWeight: priceLevel === 'B' ? 700 : 500, margin: 0 }}>B: ${parseFloat(p.priceB || 0).toFixed(2)}</p>
                          <p style={{ color: priceLevel === 'C' ? '#0077B6' : '#6B7C93', fontSize: 11, fontWeight: priceLevel === 'C' ? 700 : 500, margin: 0 }}>C: ${parseFloat(p.priceC || 0).toFixed(2)}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {lineItems.length === 0 && !showNonStockForm ? (
              <div style={{ textAlign: 'center', padding: '32px 20px', border: '2px dashed #E3ECF5', borderRadius: 14, marginTop: 14, color: '#6B7C93' }}>
                <p style={{ fontSize: 16, margin: 0 }}>No items added yet.</p>
                <p style={{ fontSize: 13, marginTop: 4, marginBottom: 0 }}>Search products, add non-stock, or import from the estimator/templates.</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto', marginTop: 14 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ background: '#E6F1FB' }}>
                    <tr>
                      <th style={{ ...tableHeaderCell, color: '#6B7C93', borderBottom: '1px solid #E3ECF5' }}>SKU</th>
                      <th style={{ ...tableHeaderCell, color: '#6B7C93', borderBottom: '1px solid #E3ECF5' }}>Description</th>
                      <th style={{ ...tableHeaderCell, color: '#6B7C93', borderBottom: '1px solid #E3ECF5', textAlign: 'center' }}>Unit</th>
                      <th style={{ ...tableHeaderCell, color: '#6B7C93', borderBottom: '1px solid #E3ECF5', textAlign: 'center' }}>Qty</th>
                      <th style={{ ...tableHeaderCell, color: '#6B7C93', borderBottom: '1px solid #E3ECF5', textAlign: 'right' }}>Unit Price</th>
                      <th style={{ ...tableHeaderCell, color: '#6B7C93', borderBottom: '1px solid #E3ECF5', textAlign: 'right' }}>Total</th>
                      <th style={{ ...tableHeaderCell, color: '#6B7C93', borderBottom: '1px solid #E3ECF5', textAlign: 'center' }}>Remove</th>
                    </tr>
                  </thead>
                  <tbody>
                    {showNonStockForm && (
                      <tr style={{ background: '#CDE7F5', borderBottom: '2px solid #E3ECF5' }}>
                        <td style={{ padding: '8px 12px' }}>
                          <span style={{ background: '#FDF1DD', color: '#854F0B', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>NON-STOCK</span>
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <input type="text" placeholder="Enter item description..."
                            value={nonStockForm.description}
                            onChange={(e) => setNonStockForm(f => ({ ...f, description: e.target.value }))}
                            onKeyDown={(e) => e.key === 'Enter' && confirmNonStockItem()}
                            onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                            onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                            style={{ width: '100%', padding: '6px 10px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s, box-shadow 0.15s' }} autoFocus />
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <select value={nonStockForm.unit} onChange={(e) => setNonStockForm(f => ({ ...f, unit: e.target.value }))} className="dark-select"
                            style={{ padding: '6px 8px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 12, width: 'auto', cursor: 'pointer', outline: 'none', fontFamily: 'inherit' }}>
                            {['pc','ft','roll','bag','lb','ea','lot'].map(u => <option key={u}>{u}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <input type="number" value={nonStockForm.qty}
                            onChange={(e) => setNonStockForm(f => ({ ...f, qty: e.target.value }))}
                            onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                            onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                            style={{ width: 72, padding: '6px 8px', textAlign: 'center', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s, box-shadow 0.15s' }} min="0.01" step="0.01" />
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                            <input type="number" value={nonStockForm.cost}
                              onChange={(e) => setNonStockForm(f => ({ ...f, cost: e.target.value }))}
                              onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                              onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                              title="COST (per unit)"
                              style={{ width: 100, padding: '6px 8px', textAlign: 'right', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s, box-shadow 0.15s' }} step="0.01" placeholder="COST" />
                            <input type="number" value={nonStockForm.unitPrice}
                              onChange={(e) => setNonStockForm(f => ({ ...f, unitPrice: e.target.value }))}
                              onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                              onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                              style={{ width: 100, padding: '6px 8px', textAlign: 'right', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s, box-shadow 0.15s' }} step="0.01" placeholder="0.00" />
                          </div>
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#0077B6', fontWeight: 700, fontSize: 13 }}>
                          ${round2((parseFloat(nonStockForm.qty)||0)*(parseFloat(nonStockForm.unitPrice)||0)).toFixed(2)}
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button onClick={confirmNonStockItem} style={{ background: '#0077B6', border: 'none', color: '#fff', padding: '4px 10px', fontSize: 11, borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Add</button>
                            <button onClick={() => { setShowNonStockForm(false); setNonStockForm({ description: '', qty: 1, unit: 'pc', unitPrice: '', cost: '' }); }}
                              style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#6B7C93', padding: '4px 10px', fontSize: 11, borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {(() => {
                      const rows = [];
                      let lastGroupId = null;
                      lineItems.forEach((item, index) => {
                        // Text/Note — SAME column structure as a normal row: text input
                        // in the Description cell only, other cells blank, ✕ remove in
                        // its usual last cell. The blank cells give a clickable area to
                        // SELECT the row so the ▲▼ reorder toolbar can move it.
                        if (item.isTextNote) {
                          rows.push(
                            <tr key={item.id}
                              onClick={(e) => {
                                if (e.target.closest('input, button, select, textarea, label')) return;
                                setSelectedLineId(item.id);
                              }}
                              style={{ background: selectedLineId === item.id ? '#E6F1FB' : (index % 2 === 0 ? '#FFFFFF' : '#F8FBFE'), boxShadow: selectedLineId === item.id ? 'inset 3px 0 0 #0077B6' : undefined, borderBottom: '1px solid #E3ECF5', cursor: 'pointer' }}>
                              <td style={{ padding: '10px 14px' }}></td>
                              <td style={{ padding: '10px 14px' }}>
                                <input type="text" value={item.description}
                                  onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                                  placeholder="Note / heading…"
                                  onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                                  onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                                  style={{ width: '100%', padding: '6px 10px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 12, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s, box-shadow 0.15s' }} />
                              </td>
                              <td style={{ padding: '10px 14px' }}></td>
                              <td style={{ padding: '10px 14px' }}></td>
                              <td style={{ padding: '10px 14px' }}></td>
                              <td style={{ padding: '10px 14px' }}></td>
                              <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                <button onClick={() => removeLineItem(item.id)} style={{ background: '#FBECEC', border: '1px solid #E9C3C3', color: '#A32D2D', padding: '4px 10px', fontSize: 11, borderRadius: 10, fontWeight: 600, cursor: 'pointer' }}>✕</button>
                              </td>
                            </tr>
                          );
                          return;
                        }
                        const groupId = item.templateBundleId || item.stockBundleId || null;
                        const groupName = item.templateBundleName || item.stockBundleName;
                        const isStockBundle = !!item.stockBundleId;
                        if (groupId && groupId !== lastGroupId) {
                          rows.push(
                            <tr key={`bundle-${groupId}-${index}`} style={{ background: isStockBundle ? '#E6F1FB' : '#E0F7FA' }}>
                              <td colSpan={7} style={{ padding: '10px 16px' }}>
                                <p style={{ fontWeight: 700, fontSize: 13, margin: 0, display: 'flex', alignItems: 'center', gap: 8, color: isStockBundle ? '#0077B6' : '#0096C7' }}>
                                  {isStockBundle ? '📦' : '📋'} {groupName}
                                </p>
                              </td>
                            </tr>
                          );
                          lastGroupId = groupId;
                        } else if (!groupId) {
                          lastGroupId = null;
                        }
                        const bundleStyle = groupId ? { borderLeft: `3px solid ${isStockBundle ? '#0077B6' : '#0096C7'}` } : {};
                        const rowBg = item.fromEstimator ? '#F0F7FC'
                          : item.fromStockBundle ? '#F0F7FC'
                          : item.fromTemplate ? '#F3F6FC'
                          : item.isNonStockCustom ? '#FCF7EE'
                          : (index % 2 === 0 ? '#FFFFFF' : '#F8FBFE');
                        rows.push(
                          <tr key={item.id}
                            onClick={(e) => {
                              // Clicks on inline controls (inputs, checkbox+label, remove) must
                              // not select the row — let them behave normally.
                              if (e.target.closest('input, button, select, textarea, label')) return;
                              setSelectedLineId(item.id);
                            }}
                            onDoubleClick={(e) => {
                              if (e.target.closest('input, button, select, textarea, label')) return;
                              openItemEditor(item);
                            }}
                            style={{ ...bundleStyle, background: selectedLineId === item.id ? '#E6F1FB' : rowBg, boxShadow: selectedLineId === item.id ? 'inset 3px 0 0 #0077B6' : undefined, borderBottom: '1px solid #E3ECF5', cursor: 'pointer' }}>
                            <td style={{ padding: '10px 14px', color: '#0077B6', fontFamily: 'monospace', fontWeight: 700, fontSize: 11 }}>{item.sku}</td>
                            <td style={{ padding: '10px 14px', color: '#03045E', fontSize: 13 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {item.isNonStockCustom && (
                                  <span style={{ background: '#FDF1DD', color: '#854F0B', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, flexShrink: 0 }}>NON-STOCK</span>
                                )}
                                {item.fromTemplate && (
                                  <span style={{ background: '#E0F7FA', color: '#0096C7', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, flexShrink: 0 }} title={item.templateBundleName || item.templateName}>📋 TEMPLATE</span>
                                )}
                                {item.fromStockBundle && (
                                  <span style={{ background: '#E6F1FB', color: '#0077B6', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, flexShrink: 0 }} title={item.stockBundleName}>📦 BUNDLE</span>
                                )}
                                {item.fromEstimator && !item.fromTemplate && !item.fromStockBundle && (
                                  <span title="From Estimator" style={{ color: '#0077B6', fontSize: 11, flexShrink: 0 }}>🔧</span>
                                )}
                                {item.itemPriceLevel && (
                                  <span title={`Per-item Level ${item.itemPriceLevel}`} style={{
                                    fontSize: 10, fontWeight: 700, flexShrink: 0,
                                    color: item.itemPriceLevel === 'A' ? '#0077B6' : item.itemPriceLevel === 'B' ? '#0F6E56' : '#854F0B',
                                    background: item.itemPriceLevel === 'A' ? '#E6F1FB' : item.itemPriceLevel === 'B' ? '#E6F7EE' : '#FDF1DD',
                                    padding: '2px 6px', borderRadius: 4,
                                  }}>[{item.itemPriceLevel}]</span>
                                )}
                                {item.isReturnCredit && (
                                  <span style={{ background: '#FBECEC', color: '#A32D2D', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, flexShrink: 0 }}>↩ RETURN</span>
                                )}
                                <input type="text" value={item.description}
                                  onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                                  onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                                  onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                                  style={{ width: '100%', padding: '6px 10px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 12, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s, box-shadow 0.15s' }} />
                              </div>
                              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, cursor: 'pointer', fontSize: 11, color: item.isReturnCredit ? '#A32D2D' : '#6B7C93', fontWeight: 600 }}>
                                <input type="checkbox" checked={!!item.isReturnCredit}
                                  onChange={(e) => updateLineItem(item.id, 'isReturnCredit', e.target.checked)}
                                  style={{ accentColor: '#A32D2D', width: 14, height: 14 }} />
                                Return Credit to Customer
                              </label>
                              {/* ON PLATE — on EVERY product line, not just the
                                  ones that look like posts. The user ticks it
                                  where it belongs; nothing here guesses. */}
                              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, marginLeft: 14, cursor: 'pointer', fontSize: 11, color: isOnPlate(item) ? '#0F6E56' : '#6B7C93', fontWeight: 600 }}>
                                <input type="checkbox" checked={isOnPlate(item)}
                                  onChange={(e) => toggleOnPlate(item.id, e.target.checked)}
                                  style={{ accentColor: '#0F6E56', width: 14, height: 14 }} />
                                On Plate (+${ON_PLATE_SURCHARGE} each)
                              </label>
                            </td>
                            <td style={{ padding: '10px 14px', textAlign: 'center', color: '#6B7C93', fontSize: 12 }}>{item.unit}</td>
                            <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                              <input type="number" value={item.qty}
                                onChange={(e) => updateLineItem(item.id, 'qty', e.target.value)}
                                onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                                onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                                style={{ width: 64, padding: '6px 8px', textAlign: 'center', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s, box-shadow 0.15s' }} min="1" />
                            </td>
                            <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <input type="number" value={item.unitPrice}
                                  onChange={(e) => updateLineItem(item.id, 'unitPrice', e.target.value)}
                                  onFocus={(e) => e.target.select()}
                                  style={{ width: 96, padding: '6px 8px', textAlign: 'right', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s, box-shadow 0.15s', color: item.manualPrice ? '#0F6E56' : '#03045E' }} step="0.01" />
                                {item.manualPrice && (
                                  <>
                                    <span title="Manual price — won't change with price level" style={{ fontSize: 11, color: '#854F0B' }}>✏️</span>
                                    <button onClick={() => resetItemPrice(item.id)}
                                      title="Reset to price level pricing"
                                      style={{ background: 'transparent', border: 'none', color: '#6B7C93', fontSize: 14, cursor: 'pointer', padding: 0, lineHeight: 1 }}
                                      onMouseEnter={e => e.currentTarget.style.color = '#03045E'}
                                      onMouseLeave={e => e.currentTarget.style.color = '#6B7C93'}
                                    >↺</button>
                                  </>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '10px 14px', textAlign: 'right', color: item.isReturnCredit ? '#A32D2D' : '#0077B6', fontWeight: 700, fontSize: 13 }}>
                              {item.isReturnCredit ? '-' : ''}${Math.abs(lineTotal(item)).toFixed(2)}
                            </td>
                            <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                              <button onClick={() => removeLineItem(item.id)} style={{ background: '#FBECEC', border: '1px solid #E9C3C3', color: '#A32D2D', padding: '4px 10px', fontSize: 11, borderRadius: 10, fontWeight: 600, cursor: 'pointer' }}>✕</button>
                            </td>
                          </tr>
                        );
                      });
                      return rows;
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Delivery + Notes */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20, marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: isDelivery ? 14 : 0 }}>
                <input type="checkbox" checked={isDelivery} onChange={(e) => setIsDelivery(e.target.checked)} style={{ accentColor: '#0077B6', width: 18, height: 18 }} />
                <span style={{ color: '#03045E', fontSize: 14, fontWeight: 600 }}>🚚 Delivery Order</span>
              </label>
              {isDelivery && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div>
                    <label style={{ color: '#6B7C93', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>Delivery Fee ($)</label>
                    <input type="number" step="0.01" value={deliveryFee}
                      onChange={(e) => setDeliveryFee(e.target.value)}
                      placeholder="0.00"
                      style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }}
                      onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }} />
                  </div>
                  <div>
                    <label style={{ color: '#6B7C93', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>Delivery Address</label>
                    <input type="text" value={deliveryStreet}
                      onChange={(e) => setDeliveryStreet(e.target.value)}
                      placeholder="Street Address"
                      style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s', marginBottom: 8 }}
                      onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }} />
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
                      <input type="text" value={deliveryCity}
                        onChange={(e) => setDeliveryCity(e.target.value)}
                        placeholder="City"
                        style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }}
                        onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                        onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }} />
                      <input type="text" value={deliveryState}
                        onChange={(e) => setDeliveryState(e.target.value)}
                        placeholder="State"
                        style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }}
                        onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                        onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }} />
                      <input type="text" value={deliveryZip}
                        onChange={(e) => setDeliveryZip(e.target.value)}
                        placeholder="ZIP"
                        style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }}
                        onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                        onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }} />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20, marginBottom: 16 }}>
              <label style={{ color: '#6B7C93', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>📝 Notes</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
                placeholder="Internal notes about this document..."
                style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s', resize: 'vertical', minHeight: 90 }}
                onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }} />
            </div>
          </div>

          {/* Totals */}
          <div style={{
            background: '#FFFFFF',
            border: '1px solid #E3ECF5',
            borderRadius: 16, padding: 24, marginBottom: 16,
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 14 }}>
              <input type="checkbox" checked={docTaxExempt} onChange={e => setDocTaxExempt(e.target.checked)} style={{ accentColor: '#0077B6', width: 18, height: 18 }} />
              <span style={{ color: '#03045E', fontSize: 14, fontWeight: 600 }}>Tax Exempt (this document)</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 14 }}>
              <input type="checkbox" checked={discountEnabled}
                onChange={e => { const on = e.target.checked; setDiscountEnabled(on); if (!on) setDiscountPercent(0); }}
                style={{ accentColor: '#0077B6', width: 18, height: 18 }} />
              <span style={{ color: '#03045E', fontSize: 14, fontWeight: 600 }}>Discount</span>
            </label>
            {discountEnabled && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10, marginBottom: 14 }}>
                <span style={{ color: '#03045E', fontSize: 14, fontWeight: 600, flex: '0 0 auto' }}>Discount (%)</span>
                <input type="number" min="0" max="100" step="0.1" value={discountPercent}
                  onChange={e => setDiscountPercent(e.target.value)}
                  style={{ flex: '0 0 90px', width: 90, maxWidth: 90, padding: '8px 10px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 8, color: '#03045E', fontSize: 14, outline: 'none', textAlign: 'right', boxSizing: 'border-box', fontFamily: 'inherit' }}
                  onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                  onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }} />
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ color: '#6B7C93', fontSize: 14 }}>Subtotal</span>
              <span style={{ color: '#03045E', fontWeight: 600, fontSize: 15 }}>${subtotal.toFixed(2)}</span>
            </div>
            {discountAmount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#6B7C93', fontSize: 14 }}>Discount ({parseFloat(discountPercent) || 0}%)</span>
                <span style={{ color: '#A32D2D', fontWeight: 600, fontSize: 15 }}>−${discountAmount.toFixed(2)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ color: '#6B7C93', fontSize: 14 }}>
                Tax (6% MD) {docTaxExempt && <span style={{ color: '#0F6E56', fontWeight: 700 }}>— EXEMPT</span>}
              </span>
              <span style={{ color: '#03045E', fontWeight: 600, fontSize: 15 }}>${tax.toFixed(2)}</span>
            </div>
            {isDelivery && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#6B7C93', fontSize: 14 }}>Delivery Fee</span>
                <span style={{ color: '#03045E', fontWeight: 600, fontSize: 15 }}>${delivery.toFixed(2)}</span>
              </div>
            )}
            <div style={{ marginTop: 14, padding: '14px 16px', background: '#E6F1FB', borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#03045E', fontSize: 14, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700 }}>Grand Total</span>
              <span style={{ fontSize: 36, fontWeight: 800, lineHeight: 1, color: '#0077B6' }}>
                ${grandTotal.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Payment — invoices only */}
          {docType === 'invoice' && (
            <div style={{ background: '#EEEDFE', border: '1px solid #CECBF6', borderRadius: 16, padding: 20, marginBottom: 16, borderLeft: `4px solid ${payStatus.status === PAYMENT_STATUS.CREDIT ? '#0077B6' : payStatus.status === PAYMENT_STATUS.PAID ? '#10b981' : payStatus.status === PAYMENT_STATUS.PARTIAL ? '#f59e0b' : '#ef4444'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                <h3 style={{ color: '#3C3489', fontSize: 16, fontWeight: 700, margin: 0 }}>💳 Payment</h3>
                {payStatus.status === PAYMENT_STATUS.CREDIT ? (
                  <span style={{ background: '#E6F1FB', color: '#0077B6', padding: '3px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>CREDIT 💙</span>
                ) : payStatus.status === PAYMENT_STATUS.PAID ? (
                  <span style={{ background: '#E6F7EE', color: '#0F6E56', padding: '3px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>PAID IN FULL ✅</span>
                ) : payStatus.status === PAYMENT_STATUS.PARTIAL ? (
                  <span style={{ background: '#FDF1DD', color: '#854F0B', padding: '3px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>PARTIAL 🟡</span>
                ) : (
                  <span style={{ background: '#FBECEC', color: '#A32D2D', padding: '3px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>UNPAID 🔴</span>
                )}
              </div>
              {payments.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  {payments.map((p, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#FFFFFF', border: '1px solid #CECBF6', borderRadius: 10, padding: '10px 14px', marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, color: '#26215C' }}>{p.method === 'Cash' ? '💵' : p.method === 'Card' ? '💳' : '🏦'} {p.method}</span>
                        <span style={{ fontWeight: 700, color: '#26215C' }}>${parseFloat(p.amount || 0).toFixed(2)}</span>
                        <span style={{ color: '#6A6391' }}>{new Date(p.date + 'T12:00:00').toLocaleDateString('en-US')}</span>
                        {p.method === 'Check' && p.checkNumber && (<span style={{ color: '#6A6391', fontSize: 11 }}>Check #{p.checkNumber}</span>)}
                        {p.notes && (<span style={{ color: '#6A6391', fontSize: 11, fontStyle: 'italic' }}>{p.notes}</span>)}
                      </div>
                      <button type="button"
                        onClick={() => setPayments(prev => prev.filter((_, idx) => idx !== i))}
                        style={{ background: 'transparent', border: 'none', color: '#6A6391', fontSize: 16, lineHeight: 1, cursor: 'pointer', marginLeft: 12 }}
                        onMouseEnter={e => e.currentTarget.style.color = '#A32D2D'}
                        onMouseLeave={e => e.currentTarget.style.color = '#6A6391'}
                        title="Remove payment">✕</button>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid #CECBF6', marginTop: 8 }}>
                    <span style={{ color: '#0F6E56', fontWeight: 700, fontSize: 13 }}>Total Paid: ${totalPaid.toFixed(2)}</span>
                    {balanceDue > 0 && (<span style={{ color: '#A32D2D', fontWeight: 700, fontSize: 13 }}>Balance Due: ${balanceDue.toFixed(2)}</span>)}
                    {creditAmount > 0 && (<span style={{ color: '#0077B6', fontWeight: 700, fontSize: 13 }}>Credit: ${creditAmount.toFixed(2)} overpaid</span>)}
                  </div>
                </div>
              )}
              {showPaymentForm && (
                <div style={{ background: '#FFFFFF', border: '1px solid #CECBF6', borderRadius: 12, padding: 16, marginBottom: 14 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
                    {/* Cash takes "Cash Received" instead of "Amount" — the recorded
                        payment is min(received, balanceDue), so a partial cash payment
                        still behaves as before and any excess shows as change to return. */}
                    {isCash ? (
                      <div>
                        <label style={{ color: '#6A6391', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>Cash Received</label>
                        <input type="number" step="0.01" value={currentPayment.cashReceived}
                          onChange={(e) => setCurrentPayment(f => ({ ...f, cashReceived: e.target.value }))}
                          placeholder={balanceDue.toFixed(2)} autoFocus
                          style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #CECBF6', borderRadius: 10, color: '#26215C', fontSize: 18, fontWeight: 700, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }}
                          onFocus={(e) => { e.target.style.borderColor = '#534AB7'; e.target.style.boxShadow = '0 0 0 3px rgba(83,74,183,0.15)'; }}
                          onBlur={(e) => { e.target.style.borderColor = '#CECBF6'; e.target.style.boxShadow = 'none'; }} />
                        {cashReceived > 0 && recordedAmount < balanceDue && (
                          <p style={{ color: '#854F0B', fontSize: 11, fontWeight: 600, marginTop: 4, marginBottom: 0 }}>
                            ⚠️ Still owing: ${Math.max(0, balanceDue - recordedAmount).toFixed(2)}
                          </p>
                        )}
                        {changeDue >= 0.01 && (
                          <p style={{ color: '#0F6E56', fontSize: 13, fontWeight: 700, marginTop: 4, marginBottom: 0 }}>
                            💵 Change: ${changeDue.toFixed(2)}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <label style={{ color: '#6A6391', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>Amount</label>
                        <input type="number" step="0.01" value={currentPayment.amount}
                          onChange={(e) => setCurrentPayment(f => ({ ...f, amount: e.target.value }))}
                          placeholder={balanceDue.toFixed(2)} autoFocus
                          style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #CECBF6', borderRadius: 10, color: '#26215C', fontSize: 18, fontWeight: 700, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }}
                          onFocus={(e) => { e.target.style.borderColor = '#534AB7'; e.target.style.boxShadow = '0 0 0 3px rgba(83,74,183,0.15)'; }}
                          onBlur={(e) => { e.target.style.borderColor = '#CECBF6'; e.target.style.boxShadow = 'none'; }} />
                        {currentPayment.amount && parseFloat(currentPayment.amount) < balanceDue && (
                          <p style={{ color: '#854F0B', fontSize: 11, fontWeight: 600, marginTop: 4, marginBottom: 0 }}>
                            ⚠️ Still owing: ${Math.max(0, balanceDue - parseFloat(currentPayment.amount)).toFixed(2)}
                          </p>
                        )}
                        {/* Not capped or refused: more than the balance is staged as
                            entered and shows as a credit — said before it is added. */}
                        {recordedAmount > balanceDue && (
                          <p style={{ color: '#0077B6', fontSize: 11, fontWeight: 600, marginTop: 4, marginBottom: 0 }}>
                            💙 Overpayment — ${round2(recordedAmount - balanceDue).toFixed(2)} will show as a credit
                          </p>
                        )}
                      </div>
                    )}
                    <div>
                      <label style={{ color: '#6A6391', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>Date</label>
                      <input type="date" value={currentPayment.date}
                        onChange={(e) => setCurrentPayment(f => ({ ...f, date: e.target.value }))}
                        style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #CECBF6', borderRadius: 10, color: '#26215C', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }}
                        onFocus={(e) => { e.target.style.borderColor = '#534AB7'; e.target.style.boxShadow = '0 0 0 3px rgba(83,74,183,0.15)'; }}
                        onBlur={(e) => { e.target.style.borderColor = '#CECBF6'; e.target.style.boxShadow = 'none'; }} />
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ color: '#6A6391', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>Payment Method</label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {[{ key: 'Cash', icon: '💵' }, { key: 'Card', icon: '💳' }, { key: 'Check', icon: '🏦' }].map(({ key, icon }) => {
                        const active = currentPayment.method === key;
                        return (
                          <button key={key} type="button"
                            onClick={() => setCurrentPayment(f => ({ ...f, method: key }))}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6,
                              padding: '8px 16px', borderRadius: 999,
                              fontWeight: 700, fontSize: 13, cursor: 'pointer',
                              background: active ? '#534AB7' : '#FFFFFF',
                              border: `1px solid ${active ? '#534AB7' : '#CECBF6'}`,
                              color: active ? '#FFFFFF' : '#6A6391',
                              transition: 'background 0.2s, border-color 0.2s, color 0.2s',
                            }}>
                            {icon} {key}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {currentPayment.method === 'Check' && (
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ color: '#6A6391', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>Check Number</label>
                      <input type="text" value={currentPayment.checkNumber}
                        onChange={(e) => setCurrentPayment(f => ({ ...f, checkNumber: e.target.value }))}
                        placeholder="e.g. 1234"
                        style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #CECBF6', borderRadius: 10, color: '#26215C', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }}
                        onFocus={(e) => { e.target.style.borderColor = '#534AB7'; e.target.style.boxShadow = '0 0 0 3px rgba(83,74,183,0.15)'; }}
                        onBlur={(e) => { e.target.style.borderColor = '#CECBF6'; e.target.style.boxShadow = 'none'; }} />
                    </div>
                  )}
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ color: '#6A6391', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>Notes (optional)</label>
                    <input type="text" value={currentPayment.notes}
                      onChange={(e) => setCurrentPayment(f => ({ ...f, notes: e.target.value }))}
                      placeholder="Optional"
                      style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #CECBF6', borderRadius: 10, color: '#26215C', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }}
                      onFocus={(e) => { e.target.style.borderColor = '#534AB7'; e.target.style.boxShadow = '0 0 0 3px rgba(83,74,183,0.15)'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#CECBF6'; e.target.style.boxShadow = 'none'; }} />
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button type="button"
                      onClick={() => {
                        if (!currentPayment.method) { alert('Please select a payment method.'); return; }
                        if (recordedAmount <= 0) {
                          alert(isCash ? 'Please enter the cash amount received.' : 'Please enter a valid amount.');
                          return;
                        }
                        // This is what feeds the status above and what
                        // saveAllPaymentRecords writes. Cash is staged CAPPED at the
                        // balance — the excess is change handed back, not a payment.
                        // Other methods are staged exactly as entered; an
                        // overpayment is kept and shows as a credit.
                        setPayments(prev => [...prev, {
                          ...currentPayment,
                          amount: isCash ? recordedAmount.toFixed(2) : currentPayment.amount,
                          cashReceived: isCash ? cashReceived : null,
                          changeDue: isCash ? changeDue : null,
                        }]);
                        setCurrentPayment({ amount: '', cashReceived: '', method: '', checkNumber: '', date: new Date().toISOString().split('T')[0], notes: '' });
                        setShowPaymentForm(false);
                      }}
                      style={{ background: '#534AB7', border: 'none', color: '#fff', padding: '12px 24px', borderRadius: 12, fontSize: 14, fontWeight: 700, letterSpacing: 0.5, cursor: 'pointer', transition: 'opacity 0.2s' }}>✅ Add Payment</button>
                    <button type="button"
                      onClick={() => { setShowPaymentForm(false); setCurrentPayment({ amount: '', cashReceived: '', method: '', checkNumber: '', date: new Date().toISOString().split('T')[0], notes: '' }); }}
                      style={{ background: '#FFFFFF', border: '1px solid #CECBF6', color: '#6A6391', padding: '12px 24px', borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s' }}>Cancel</button>
                  </div>
                </div>
              )}
              {!showPaymentForm && balanceDue > 0 && (
                <button type="button"
                  onClick={() => { setCurrentPayment(f => ({ ...f, amount: balanceDue.toFixed(2) })); setShowPaymentForm(true); }}
                  style={{ background: '#534AB7', border: 'none', color: '#fff', padding: '12px 24px', borderRadius: 12, fontSize: 14, fontWeight: 700, letterSpacing: 0.5, cursor: 'pointer', transition: 'opacity 0.2s' }}>
                  ➕ Record Payment {balanceDue < grandTotal && (<span style={{ color: '#FFFFFF', opacity: 0.7, fontWeight: 400, marginLeft: 6 }}>— ${balanceDue.toFixed(2)} remaining</span>)}
                </button>
              )}
            </div>
          )}

          {/* Save buttons */}
          <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button onClick={handleSave} disabled={saving || saved || savingAndConverting || !selectedCustomer || lineItems.length === 0}
                style={{
                  background: '#0077B6', border: 'none', color: '#fff', padding: '12px 24px', borderRadius: 12, fontSize: 14, fontWeight: 700, letterSpacing: 0.5, boxShadow: '0 4px 14px rgba(0,119,182,0.3)', transition: 'transform 0.3s, box-shadow 0.3s', flex: 1, minWidth: 200,
                  opacity: (saving || saved || savingAndConverting || !selectedCustomer || lineItems.length === 0) ? 0.5 : 1,
                  cursor: (saving || saved || savingAndConverting || !selectedCustomer || lineItems.length === 0) ? 'not-allowed' : 'pointer',
                }}>
                {saving ? 'Saving...' : saved ? '✅ Saved!' : `💾 Save ${docType === 'quote' ? 'Quote' : 'Invoice'}`}
              </button>
              {docType === 'quote' && (
                <button onClick={handleSaveAndConvert} disabled={savingAndConverting || saving || saved || !selectedCustomer || lineItems.length === 0}
                  style={{
                    background: '#E6F7EE', border: '1px solid #0F6E56', color: '#0F6E56', borderRadius: 12, fontWeight: 600, transition: 'background 0.3s', flex: 1, minWidth: 200, fontSize: 14, padding: '12px 24px',
                    opacity: (savingAndConverting || saving || saved || !selectedCustomer || lineItems.length === 0) ? 0.5 : 1,
                    cursor: (savingAndConverting || saving || saved || !selectedCustomer || lineItems.length === 0) ? 'not-allowed' : 'pointer',
                  }}>
                  {savingAndConverting ? 'Converting...' : '🧾 Save & Convert to Invoice'}
                </button>
              )}
              <button onClick={() => confirmLeave('/dashboard')} style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E', borderRadius: 12, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s', padding: '12px 24px', fontSize: 14 }}>← Back</button>
            </div>
            {(!selectedCustomer || lineItems.length === 0) && !saved && (
              <p style={{ color: '#6B7C93', fontSize: 12, marginTop: 10, marginBottom: 0 }}>Add a customer and at least one item to save</p>
            )}
          </div>

        </div>
      </div>

      <TemplatesPickerModal
        open={showTemplatesPicker}
        onClose={() => setShowTemplatesPicker(false)}
        onSelectCustomTemplate={addTemplateBundleToDocument}
        onSelectStockBundle={addStockBundleToDocument}
      />

      <LineItemEditorModal
        open={showItemEditor}
        item={editingItem}
        onClose={() => { setShowItemEditor(false); setEditingItem(null); }}
        onSave={saveItemEdits}
        products={products}
        globalPriceLevel={priceLevel}
      />

      <ProductPickerModal
        open={showProductPicker}
        onClose={() => setShowProductPicker(false)}
        onAdd={addProductsFromPicker}
        products={products}
        categories={CATEGORIES}
        currentPriceLevel={priceLevel}
      />

      {/* Office Use Modal */}
      {showOfficeUse && (
        <div className="print:hidden"
          style={{
            position: 'fixed', inset: 0, zIndex: 50,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
          onClick={() => setShowOfficeUse(false)}
        >
          <div
            style={{
              background: C_BG_CARD_SOLID,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${C_VIOLET}4D`,
              borderRadius: 20, padding: 28,
              maxWidth: 420, width: '100%',
              boxShadow: theme.shadowCard,
              position: 'relative',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ height: 2, background: `linear-gradient(90deg, transparent, ${C_CYAN}, ${C_VIOLET}, transparent)`, marginBottom: 20, marginLeft: -28, marginRight: -28, marginTop: -28 }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ color: C_TEXT, fontWeight: 700, fontSize: 16, margin: 0 }}>🔒 Office Use Only — Internal</h3>
              <button onClick={() => setShowOfficeUse(false)} style={{ background: 'transparent', border: 'none', color: C_TEXT_3, fontSize: 22, lineHeight: 1, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                <span style={{ color: C_TEXT_2 }}>Sale Total (pre-tax)</span>
                <span style={{ color: C_TEXT, fontWeight: 600 }}>${subtotal.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                <span style={{ color: C_TEXT_2 }}>Sales Tax (6%) <span style={{ fontSize: 10, color: C_TEXT_3 }}>(excluded from profit)</span></span>
                <span style={{ color: C_TEXT, fontWeight: 600 }}>${tax.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                <span style={{ color: C_TEXT_2 }}>Delivery Fee <span style={{ fontSize: 10, color: C_TEXT_3 }}>(excluded from profit)</span></span>
                <span style={{ color: C_TEXT, fontWeight: 600 }}>{isDelivery ? `$${delivery.toFixed(2)}` : 'N/A'}</span>
              </div>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', borderBottom: '1px solid rgba(255,255,255,0.1)', padding: '8px 0', margin: '4px 0', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: C_TEXT, fontWeight: 700 }}>Grand Total</span>
                <span style={{ color: C_CYAN_BRIGHT, fontWeight: 700 }}>${grandTotal.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                <span style={{ color: C_TEXT_2 }}>Total Cost of Materials</span>
                <span style={{ color: C_ORANGE_BRIGHT, fontWeight: 700 }}>${totalCost.toFixed(2)}</span>
              </div>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: 8, marginTop: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                  <span style={{ color: C_TEXT_2 }}>Gross Profit</span>
                  <span style={{ color: grossProfit >= 0 ? C_GREEN_BRIGHT : C_RED_BRIGHT, fontWeight: 700 }}>${grossProfit.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                  <span style={{ color: C_TEXT_2 }}>Profit Margin</span>
                  <span style={{
                    fontSize: 18, fontWeight: 800,
                    color: profitMarginPct >= 20 ? C_GREEN_BRIGHT : profitMarginPct >= 10 ? C_ORANGE_BRIGHT : C_RED_BRIGHT,
                  }}>
                    {profitMarginPct.toFixed(1)}%
                  </span>
                </div>
                <p style={{ fontSize: 10, color: C_TEXT_3, marginTop: 6, marginBottom: 0 }}>
                  Gross Profit &amp; Margin are based on the discounted pre-tax product sale — excluding tax and delivery.
                </p>
              </div>
            </div>
            <button onClick={() => setShowOfficeUse(false)} style={{ ...gradientButton, width: '100%', marginTop: 16 }}>Close</button>
            <p style={{ color: C_TEXT_3, fontSize: 11, textAlign: 'center', marginTop: 12, marginBottom: 0 }}>⚠️ Internal use only — do not share with customer</p>
          </div>
        </div>
      )}


      {/* Quick New Customer modal — create without leaving the document */}
      {showNewCustomerModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 70,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }}
          onClick={() => !creatingCustomer && setShowNewCustomerModal(false)}
        >
          <div style={{
            background: C_BG_CARD_SOLID,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${C_VIOLET}4D`,
            borderRadius: 20, padding: 28,
            maxWidth: 500, width: '100%',
            boxShadow: theme.shadowCard,
            position: 'relative',
          }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ height: 2, background: `linear-gradient(90deg, transparent, ${C_VIOLET}, ${C_CYAN}, transparent)`, marginBottom: 20, marginLeft: -28, marginRight: -28, marginTop: -28 }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ color: C_TEXT, fontWeight: 800, fontSize: 20, margin: 0 }}>+ New Customer</h3>
              <button onClick={() => setShowNewCustomerModal(false)} disabled={creatingCustomer}
                style={{ background: 'transparent', border: 'none', color: C_TEXT_3, fontSize: 22, lineHeight: 1, cursor: creatingCustomer ? 'not-allowed' : 'pointer' }}>✕</button>
            </div>
            <p style={{ color: C_TEXT_2, fontSize: 13, marginTop: 0, marginBottom: 20 }}>
              Create a new customer for this {docType === 'quote' ? 'quote' : 'invoice'}. They'll be added to your customer database.
            </p>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Full Name <span style={{ color: C_RED_BRIGHT }}>*</span></label>
              <input type="text" value={newCustomerData.name} autoFocus
                onChange={e => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
                onKeyDown={e => { if (e.key === 'Enter' && newCustomerData.name.trim() && !creatingCustomer) handleCreateAndSelectCustomer(); }}
                style={inputStyle} placeholder="John Smith"
                onFocus={focusInputCyan} onBlur={blurInputDefault} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Phone</label>
                <input type="tel" value={newCustomerData.phone}
                  onChange={e => setNewCustomerData({ ...newCustomerData, phone: e.target.value })}
                  style={inputStyle} placeholder="410-555-0123"
                  onFocus={focusInputCyan} onBlur={blurInputDefault} />
              </div>
              <div>
                <label style={labelStyle}>Email</label>
                <input type="email" value={newCustomerData.email}
                  onChange={e => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                  style={inputStyle} placeholder="optional"
                  onFocus={focusInputCyan} onBlur={blurInputDefault} />
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Address</label>
              <input type="text" value={newCustomerData.address}
                onChange={e => setNewCustomerData({ ...newCustomerData, address: e.target.value })}
                style={inputStyle} placeholder="123 Main St, Hanover MD"
                onFocus={focusInputCyan} onBlur={blurInputDefault} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Notes (optional)</label>
              <textarea value={newCustomerData.notes}
                onChange={e => setNewCustomerData({ ...newCustomerData, notes: e.target.value })}
                style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
                placeholder="Any notes about this customer..."
                onFocus={focusInputCyan} onBlur={blurInputDefault} />
            </div>

            <label style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '12px 16px', background: theme.inputBg,
              border: `1px solid ${C_BORDER}`, borderRadius: 10,
              cursor: 'pointer', marginBottom: 20,
            }}>
              <input type="checkbox" checked={newCustomerData.taxExempt}
                onChange={e => setNewCustomerData({ ...newCustomerData, taxExempt: e.target.checked })}
                style={{ accentColor: C_CYAN, width: 18, height: 18 }} />
              <span style={{ color: C_TEXT, fontSize: 14, fontWeight: 600 }}>Tax Exempt Customer</span>
            </label>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleCreateAndSelectCustomer}
                disabled={creatingCustomer || !newCustomerData.name.trim()}
                style={{
                  ...gradientButton, flex: 1,
                  opacity: (creatingCustomer || !newCustomerData.name.trim()) ? 0.5 : 1,
                  cursor: (creatingCustomer || !newCustomerData.name.trim()) ? 'not-allowed' : 'pointer',
                }}>
                {creatingCustomer ? 'Creating...' : '✓ Create & Select'}
              </button>
              <button onClick={() => setShowNewCustomerModal(false)} disabled={creatingCustomer}
                style={{ ...darkButton, flex: 1 }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Possible-duplicate warning modal */}
      {showDuplicateWarning && possibleDuplicates.length > 0 && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 80,
          background: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }}>
          <div style={{
            background: C_BG_CARD_SOLID,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${C_ORANGE}4D`,
            borderRadius: 20, padding: 28,
            maxWidth: 600, width: '100%', maxHeight: '85vh',
            display: 'flex', flexDirection: 'column',
            boxShadow: theme.shadowCard,
          }}>
            <div style={{ height: 2, background: `linear-gradient(90deg, transparent, ${C_ORANGE}, transparent)`, marginBottom: 24, marginLeft: -28, marginRight: -28, marginTop: -28 }} />
            <h3 style={{ color: C_ORANGE_BRIGHT, fontSize: 20, fontWeight: 800, margin: '0 0 8px 0' }}>
              ⚠️ Possible Duplicate Customers
            </h3>
            <p style={{ color: C_TEXT_2, fontSize: 13, marginTop: 0, marginBottom: 20 }}>
              We found existing customers that match the information you entered. Please verify if any of these are the same person.
            </p>

            <div style={{
              background: `${C_ORANGE}14`, border: `1px solid ${C_ORANGE}33`,
              borderRadius: 10, padding: '12px 16px', marginBottom: 16,
            }}>
              <p style={{ color: C_TEXT_2, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, margin: 0, fontWeight: 700 }}>You're creating:</p>
              <p style={{ color: C_TEXT, fontWeight: 700, fontSize: 15, margin: '4px 0 2px 0' }}>{pendingCustomerData?.name}</p>
              <p style={{ color: C_TEXT_2, fontSize: 12, margin: 0 }}>
                {pendingCustomerData?.phone || 'No phone'}{pendingCustomerData?.email && ` · ${pendingCustomerData.email}`}
              </p>
            </div>

            <p style={{ color: C_TEXT_2, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 10 }}>Existing customers found:</p>

            <div style={{ flex: 1, overflowY: 'auto', marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {possibleDuplicates.map(dup => (
                <div key={dup.id} style={{
                  background: theme.inputBg, border: `1px solid ${C_BORDER}`,
                  borderRadius: 12, padding: '14px 16px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                      <p style={{ color: C_TEXT, fontWeight: 700, fontSize: 14, margin: 0 }}>{dup.name}</p>
                      <span style={{ background: `${C_VIOLET}26`, color: C_VIOLET_BRIGHT, padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, fontFamily: 'monospace' }}>{dup.customerNumber}</span>
                      {dup.matchType === 'exact-name' && (
                        <span style={{ background: `${C_RED}26`, color: C_RED_BRIGHT, padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700 }}>EXACT NAME</span>
                      )}
                      {(dup.matchType === 'similar-name-same-phone' || dup.matchType === 'same-phone') && (
                        <span style={{ background: `${C_ORANGE}26`, color: C_ORANGE_BRIGHT, padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700 }}>SAME PHONE</span>
                      )}
                      {dup.matchType === 'similar-name-same-email' && (
                        <span style={{ background: `${C_ORANGE}26`, color: C_ORANGE_BRIGHT, padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700 }}>SAME EMAIL</span>
                      )}
                    </div>
                    <p style={{ color: C_TEXT_2, fontSize: 12, margin: 0 }}>
                      📞 {dup.phone || 'No phone'}{dup.email && ` · ✉️ ${dup.email}`}
                    </p>
                    {dup.address && (
                      <p style={{ color: C_TEXT_3, fontSize: 11, margin: '2px 0 0 0' }}>📍 {dup.address}</p>
                    )}
                  </div>
                  <button onClick={() => selectExistingCustomer(dup)}
                    style={{ ...cyanGlassButton, padding: '8px 14px', fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0 }}>
                    ✓ Use This
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={createAnyway} disabled={creatingCustomer}
                style={{ ...orangeGlassButton, flex: 1, padding: 12, opacity: creatingCustomer ? 0.5 : 1, cursor: creatingCustomer ? 'not-allowed' : 'pointer' }}>
                {creatingCustomer ? 'Creating...' : '+ Create Anyway as New Customer'}
              </button>
              <button onClick={() => { setShowDuplicateWarning(false); setPossibleDuplicates([]); setPendingCustomerData(null); }}
                disabled={creatingCustomer}
                style={{ ...darkButton, flex: 1, padding: 12 }}>
                ← Back to Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single-item inventory alert */}
      {stockAlertProduct && stockAlertStatus && (() => {
        const isOut = stockAlertStatus.level === 'out';
        const accent = isOut ? C_RED : C_ORANGE;
        const accentBright = isOut ? C_RED_BRIGHT : C_ORANGE_BRIGHT;
        const title = stockAlertStatus.level === 'out' ? '🔴 Out of Stock'
          : stockAlertStatus.level === 'insufficient' ? '⚠️ Not Enough Stock'
          : '🟡 Low Stock';
        const closeAlert = () => { setStockAlertProduct(null); setStockAlertStatus(null); };
        return (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 80,
            background: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}>
            <div style={{
              background: C_BG_CARD_SOLID, backdropFilter: 'blur(20px)',
              border: `1px solid ${accent}4D`, borderRadius: 20, padding: 28,
              width: '100%', maxWidth: 480, boxShadow: theme.shadowCard,
            }}>
              <div style={{ height: 2, background: `linear-gradient(90deg, transparent, ${accent}, transparent)`, marginBottom: 24, marginLeft: -28, marginRight: -28, marginTop: -28 }} />
              <h3 style={{ color: accentBright, fontSize: 20, fontWeight: 800, margin: '0 0 16px 0' }}>{title}</h3>

              <div style={{ background: theme.inputBg, border: `1px solid ${C_BORDER}`, borderRadius: 12, padding: 16, marginBottom: 20 }}>
                <p style={{ color: C_TEXT, fontWeight: 700, fontSize: 15, margin: 0 }}>{stockAlertProduct.description}</p>
                <p style={{ color: C_TEXT_2, fontSize: 12, margin: '4px 0 0 0' }}>
                  <span style={{ color: C_CYAN_BRIGHT, fontFamily: 'monospace', fontWeight: 700 }}>{stockAlertProduct.sku}</span>
                  {stockAlertProduct.category ? ` · ${stockAlertProduct.category}` : ''}
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                <div style={{ background: `${accent}1A`, border: `1px solid ${accent}4D`, borderRadius: 12, padding: 14, textAlign: 'center' }}>
                  <p style={{ color: C_TEXT_2, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, margin: 0, fontWeight: 700 }}>Currently in Stock</p>
                  <p style={{ color: accentBright, fontSize: 32, fontWeight: 800, margin: '6px 0 0 0', lineHeight: 1 }}>{stockAlertStatus.stockQty}</p>
                  <p style={{ color: C_TEXT_3, fontSize: 11, margin: '4px 0 0 0' }}>{stockAlertProduct.unit}</p>
                </div>
                <div style={{ background: `${C_CYAN}1A`, border: `1px solid ${C_CYAN}4D`, borderRadius: 12, padding: 14, textAlign: 'center' }}>
                  <p style={{ color: C_TEXT_2, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, margin: 0, fontWeight: 700 }}>Reorder Point</p>
                  <p style={{ color: C_CYAN_BRIGHT, fontSize: 32, fontWeight: 800, margin: '6px 0 0 0', lineHeight: 1 }}>{parseFloat(stockAlertProduct.reorderPoint) || 0}</p>
                  <p style={{ color: C_TEXT_3, fontSize: 11, margin: '4px 0 0 0' }}>{stockAlertProduct.unit}</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { const p = stockAlertProduct; closeAlert(); commitLineItem(p); }}
                  style={{ ...gradientButton, flex: 1 }}>✓ Add Anyway</button>
                <button onClick={closeAlert} style={{ ...darkButton, flex: 1 }}>Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Batch inventory alert (from the Add Products picker) */}
      {batchStockAlerts && batchStockAlerts.length > 0 && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 80,
          background: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div style={{
            background: C_BG_CARD_SOLID, backdropFilter: 'blur(20px)',
            border: `1px solid ${C_ORANGE}4D`, borderRadius: 20, padding: 28,
            width: '100%', maxWidth: 600, maxHeight: '80vh', display: 'flex', flexDirection: 'column',
            boxShadow: theme.shadowCard,
          }}>
            <div style={{ height: 2, background: `linear-gradient(90deg, transparent, ${C_ORANGE}, transparent)`, marginBottom: 24, marginLeft: -28, marginRight: -28, marginTop: -28 }} />
            <h3 style={{ color: C_ORANGE_BRIGHT, fontSize: 20, fontWeight: 800, margin: '0 0 8px 0' }}>⚠️ Stock Alerts</h3>
            <p style={{ color: C_TEXT_2, fontSize: 13, marginTop: 0, marginBottom: 16 }}>
              Some of the selected products have stock issues. Review before adding.
            </p>

            <div style={{ flex: 1, overflowY: 'auto', marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {batchStockAlerts.map(({ product, status }) => {
                const isOut = status.level === 'out';
                const accent = isOut ? C_RED : C_ORANGE;
                const accentBright = isOut ? C_RED_BRIGHT : C_ORANGE_BRIGHT;
                return (
                  <div key={product.id} style={{
                    background: `${accent}14`, border: `1px solid ${accent}33`, borderRadius: 10,
                    padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ color: C_TEXT, fontWeight: 700, fontSize: 14, margin: 0 }}>{product.description}</p>
                      <p style={{ color: C_TEXT_2, fontSize: 11, margin: '2px 0 0 0' }}>
                        <span style={{ color: C_CYAN_BRIGHT, fontFamily: 'monospace' }}>{product.sku}</span>
                      </p>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <p style={{ color: accentBright, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, margin: 0 }}>{status.message}</p>
                      <p style={{ color: C_TEXT, fontSize: 16, fontWeight: 700, margin: '2px 0 0 0' }}>{status.stockQty} {product.unit} left</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => commitProductsFromPicker(pendingBatchProducts)}
                style={{ ...gradientButton, flex: 1 }}>✓ Add All Anyway</button>
              <button onClick={() => { setBatchStockAlerts([]); setPendingBatchProducts(null); }}
                style={{ ...darkButton, flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default NewDocument;
